/**
 * 内容编辑页面 - 完整标书预览和生成
 */
import React, { useState, useEffect, useCallback, useRef, startTransition } from 'react';
import { OutlineData, OutlineItem } from '../types';
import { DocumentTextIcon, PlayIcon, DocumentArrowDownIcon, CheckCircleIcon, ExclamationCircleIcon, Bars3Icon, ArrowPathIcon, LinkIcon, PencilIcon, StopIcon } from '@heroicons/react/24/outline';
import { contentApi, ChapterContentRequest, documentApi, annotationApi, projectApi, companyApi, configApi } from '../services/api';
import { saveAs } from 'file-saver';
import { draftStorage } from '../utils/draftStorage';
import UnifiedEditor from '../components/UnifiedEditor';
import ConfirmDialog from '../components/ConfirmDialog';
import AnnotatedContent from '../components/AnnotatedContent';
import StyleSettingsDialog, { StyleConfig, DEFAULT_STYLE_CONFIG, GlobalConfig, DEFAULT_GLOBAL_CONFIG } from '../components/StyleSettingsDialog';
import Toast, { ToastType } from '../components/Toast';
import AIAssistantPanel from '../components/AIAssistantPanel'; // 新增：AI智能助手面板
import TokenAlertDialog from '../components/TokenAlertDialog';
import useTokenAlert from '../hooks/useTokenAlert';
import axios from 'axios';
import { getModelDisplayName, filterModels, TEXT_MODELS_CONFIG } from '../config/aiConfig';

// 将字符串转换为哈希码（整数）
const hashCode = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

interface ContentEditProps {
  outlineData: OutlineData | null;
  selectedChapter: string;
  onChapterSelect: (chapterId: string) => void;
  isStepSidebarCollapsed?: boolean; // 步骤导航是否收起
  projectId?: string; // 新增：项目ID
  projectName?: string; // 新增：项目名称
  selectedModel: string; // 新增：项目配置的模型
  tokenBalance?: number | null; // 新增：Token余额（用于检查是否支持批量生成）
  onChapterSave?: (chapterId: string, content: string) => void; // 新增：章节保存回调
  onProjectComplete?: () => void; // 新增：完成项目回调
  onRefreshOutline?: (projectId: string) => void; // 新增：刷新目录数据回调
}

interface GenerationProgress {
  total: number;
  completed: number;
  current: string;
  failed: string[];
  generating: Set<string>; // 正在生成的项目ID集合
}


const ContentEdit: React.FC<ContentEditProps> = ({
  outlineData,
  selectedChapter,
  onChapterSelect,
  isStepSidebarCollapsed = false,
  projectId, // 新增
  projectName, // 新增
  selectedModel, // 新增
  tokenBalance, // 新增：Token余额
  onChapterSave, // 新增
  onProjectComplete, // 新增
  onRefreshOutline, // 新增：刷新目录回调
}) => {

  // Token 欠费弹窗（充值跳转主站，见 useTokenAlert）
  const { tokenAlert, setTokenAlert, closeAlert: closeTokenAlert, handleRecharge: rechargeTokens } = useTokenAlert();

  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<GenerationProgress>({
    total: 0,
    completed: 0,
    current: '',
    failed: [],
    generating: new Set<string>()
  });
  const [leafItems, setLeafItems] = useState<OutlineItem[]>([]);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showOutlineNav, setShowOutlineNav] = useState(true); // 控制左侧目录显示
  const [activeSection, setActiveSection] = useState<string>(''); // 当前激活的章节ID
  const [editingChapter, setEditingChapter] = useState<string | null>(null); // 正在编辑的章节ID
  const [editContent, setEditContent] = useState<string>(''); // 编辑中的内容
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(null); // 自动保存计时器
  const [showTopBar, setShowTopBar] = useState(true); // 控制顶部面板显示
  const lastScrollYRef = useRef(0); // 使用 ref 记录上次滚动位置
  const [activeChapterId, setActiveChapterId] = useState<string>(''); // 当前激活的章节ID
  const isManualScrollingRef = useRef(false); // 标记是否为手动点击滚动
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null); // 滚动防抖定时器
  
  // 全局批注导航状态
  const [allAnnotations, setAllAnnotations] = useState<any[]>([]); // 所有章节的批注
  const [showAnnotationNav, setShowAnnotationNav] = useState(true); // 显示批注导航栏
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false); // 控制右侧批注面板显示/隐藏 - 默认收起
  
  // 资源关联面板状态
  const [activeResourceChapter, setActiveResourceChapter] = useState<string | null>(null); // 当前显示资源面板的章节ID
  const [activeResourceChapterData, setActiveResourceChapterData] = useState<OutlineItem | null>(null); // 当前资源面板对应的章节数据
  const [editingResourceWordCount, setEditingResourceWordCount] = useState<string | null>(null); // 正在编辑字数的章节ID
  const [tempResourceWordCount, setTempResourceWordCount] = useState<string>(''); // 临时字数值
  const [projectFiles, setProjectFiles] = useState<{tender?: any, others: any[]}>({others: []}); // 项目文件
  const [availableTextModels, setAvailableTextModels] = useState<string[]>([]); // 可用的文本模型列表
  const [supplementFiles, setSupplementFiles] = useState<Array<{
    id: string; 
    file_id: string;
    name: string; 
    size: number;
    usage_note: string;
    status: string;
    processing_method?: string;
    summary_length?: number;
    created_at: string;
  }>>([]); // 补充材料文件列表
  const [uploadingSupplement, setUploadingSupplement] = useState(false); // 补充材料上传中
  const supplementFileInputRef = useRef<HTMLInputElement>(null); // 补充材料文件输入框引用
  const [loadingResources, setLoadingResources] = useState(false); // 资源加载中
  const [chaptersWithProcessingFiles, setChaptersWithProcessingFiles] = useState<Set<string>>(new Set()); // 有处理中文件的章节ID集合
  const [chapterResources, setChapterResources] = useState<Record<string, {
    use_project_overview: boolean;
    use_tech_requirements: boolean;
    use_disqualification: boolean;
    use_quotation: boolean;
    use_templates: boolean;
    use_tender_document: boolean;
    use_other_materials: boolean;
    selected_other_materials: string[];
    selected_model?: string;
    generation_preferences: any;
    suggested?: {
      tech_requirements: boolean;
      quotation: boolean;
      templates: boolean;
    };
  }>>({});
  
  // AI 助手面板状态
  const [aiPanelExpanded, setAiPanelExpanded] = useState(false); // 是否展开，默认收缩为小球
  const [aiPanelPosition, setAiPanelPosition] = useState({ x: window.innerWidth - 340, y: 90 }); // 默认位置 - 右侧距顶部90px、距右边20px
  const [isDraggingAiPanel, setIsDraggingAiPanel] = useState(false);
  const [hasDragged, setHasDragged] = useState(false); // 是否真正拖动过
  const [aiCheckScore, setAiCheckScore] = useState(100); // AI检查健康分数，与面板同步
  const aiPanelDragStartRef = useRef({ x: 0, y: 0, panelX: 0, panelY: 0 });
  const aiPanelLongPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const stopGenerationRef = useRef(false); // 停止生成标志
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map()); // 存储每个章节的AbortController
  
  // Token 欠费警告后继续生成的相关状态
  const pendingOverdraftChapterRef = useRef<OutlineItem | null>(null); // 因欠费警告而暂停的章节
  const forceOverdraftRef = useRef(false); // 是否强制继续生成（忽略欠费警告）

  // 样式设置状态
  const [showStyleDialog, setShowStyleDialog] = useState(false);
  const [styleConfig, setStyleConfig] = useState<StyleConfig>(DEFAULT_STYLE_CONFIG);
  const [globalConfig, setGlobalConfig] = useState<GlobalConfig>(DEFAULT_GLOBAL_CONFIG);
  
  // Toast提示状态
  const [toast, setToast] = useState<{
    show: boolean;
    type: ToastType;
    title: string;
    message?: string;
  }>({
    show: false,
    type: 'info',
    title: '',
    message: ''
  });
  
  // 公司信息状态
  const [businessLicenseUrl, setBusinessLicenseUrl] = useState<string>();

  // 显示Toast提示
  const showToast = useCallback((type: ToastType, title: string, message?: string) => {
    setToast({ show: true, type, title, message });
  }, []);

  // 关闭Toast
  const closeToast = useCallback(() => {
    setToast(prev => ({ ...prev, show: false }));
  }, []);
  
  // 加载保存的样式配置
  useEffect(() => {
    const savedConfig = localStorage.getItem('bidding_style_config');
    if (savedConfig) {
      try {
        setStyleConfig(JSON.parse(savedConfig));
      } catch (e) {
        // 加载失败使用默认配置
      }
    }
    // 加载全局配置（优先从后端加载）
    const loadGlobalConfig = async () => {
      if (projectId) {
        try {
          const response = await projectApi.getAIDirection(projectId);
          if (response.data?.success && response.data?.data?.ai_direction !== undefined) {
            setGlobalConfig({ aiDirection: response.data.data.ai_direction || '' });
          } else {
            // 后端没有，尝试从 localStorage 加载
            const savedGlobalConfig = localStorage.getItem('bidding_global_config');
            if (savedGlobalConfig) {
              setGlobalConfig(JSON.parse(savedGlobalConfig));
            }
          }
        } catch (error) {
          console.error('加载AI生成方向失败:', error);
          // 出错时从 localStorage 加载
          const savedGlobalConfig = localStorage.getItem('bidding_global_config');
          if (savedGlobalConfig) {
            setGlobalConfig(JSON.parse(savedGlobalConfig));
          }
        }
      } else {
        // 没有 projectId，从 localStorage 加载
        const savedGlobalConfig = localStorage.getItem('bidding_global_config');
        if (savedGlobalConfig) {
          setGlobalConfig(JSON.parse(savedGlobalConfig));
        }
      }
    };
    loadGlobalConfig();
  }, [projectId]);

  // 确认对话框状态
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    showSecondButton?: boolean; // 是否显示第二个操作按钮
    secondButtonText?: string; // 第二个按钮的文案
    secondButtonAction?: () => void; // 第二个按钮的操作
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // 收集所有叶子节点
  const collectLeafItems = useCallback((items: OutlineItem[]): OutlineItem[] => {
    let leaves: OutlineItem[] = [];
    items.forEach(item => {
      if (!item.children || item.children.length === 0) {
        leaves.push(item);
      } else {
        leaves = leaves.concat(collectLeafItems(item.children));
      }
    });
    return leaves;
  }, []);

  // 加载章节资源配置（仅加载勾选状态，不加载文件列表）
  const loadChapterResources = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoadingResources(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await axios.get(
        `${API_BASE_URL}/api/projects/${projectId}/chapter-resources`
      );
      
      if (response.data.success) {
        setChapterResources(response.data.data);
        // ✅ 不再遍历所有章节，延迟到用户打开资源面板时再加载
      }
    } catch (error) {
      // 加载失败不影响页面显示
    } finally {
      setLoadingResources(false);
    }
  }, [projectId]);

  // 加载指定章节的补充材料
  const loadSupplementFiles = useCallback(async (chapterId: string) => {
    if (!projectId) return;
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await axios.get(
        `${API_BASE_URL}/api/projects/${projectId}/chapter-resources/${chapterId}/supplement-files`
      );
      
      if (response.data.success) {
        const files = response.data.data;
        setSupplementFiles(files);
        
        // 检查是否有处理中的文件
        const hasProcessing = files.some((file: any) => 
          file.status === 'pending' || file.status === 'processing'
        );
        
        // 更新章节处理状态
        setChaptersWithProcessingFiles(prev => {
          const newSet = new Set(prev);
          if (hasProcessing) {
            newSet.add(chapterId);
          } else {
            newSet.delete(chapterId);
          }
          return newSet;
        });
      }
    } catch (error) {
      // 加载失败不影响页面显示
    }
  }, [projectId]);

  // 初始化加载资源配置
  useEffect(() => {
    if (projectId) {
      loadChapterResources();
    }
  }, [projectId, loadChapterResources]);

  // 当切换章节时加载该章节的补充材料
  useEffect(() => {
    if (activeResourceChapter) {
      loadSupplementFiles(activeResourceChapter);
    }
  }, [activeResourceChapter, loadSupplementFiles]);
  
  // 定时检查处理中的文件（仅在有处理中文件时轮询）
  useEffect(() => {
    // 只有当资源面板打开且有处理中文件时才启动轮询
    if (!activeResourceChapter) {
      return; // 资源面板未打开，不轮询
    }
    
    // 检查当前章节是否有处理中的文件
    const hasProcessing = chaptersWithProcessingFiles.has(activeResourceChapter);
    
    if (!hasProcessing) {
      return; // 没有处理中的文件，不需要轮询
    }
    
    // 设置定时器，每5秒检查一次文件处理状态
    const intervalId = setInterval(() => {
      loadSupplementFiles(activeResourceChapter);
    }, 5000); // 改为5秒，减少请求频率
    
    return () => clearInterval(intervalId);
  }, [activeResourceChapter, chaptersWithProcessingFiles, loadSupplementFiles]);

  // 切换资源选项（纯本地状态更新，保存时统一提交）
  const handleToggleResourceOption = useCallback((chapterId: string, option: string) => {
    if (!chapterId) return;
    
    setChapterResources(prev => {
      if (!prev[chapterId]) return prev;
      const currentValue = (prev[chapterId] as any)?.[option] || false;
      return {
        ...prev,
        [chapterId]: {
          ...prev[chapterId],
          [option]: !currentValue
        } as any
      };
    });
  }, []);

  // 获取章节资源配置（带默认值）
  const getChapterResource = useCallback((chapterId: string) => {
    const resource = chapterResources[chapterId];
    if (resource) {
      return resource;
    }
    // 如果没有配置，返回空对象（所有选项为false）
    // 这样会触发handleToggleResource中的自动匹配
    return {
      use_project_overview: false,
      use_tech_requirements: false,
      use_disqualification: false,
      use_quotation: false,
      use_templates: false,
      use_tender_document: false,
      use_other_materials: false,
      selected_other_materials: [],
      selected_model: undefined,
      generation_preferences: {},
      suggested: {
        tech_requirements: false,
        quotation: false,
        templates: false
      }
    };
  }, [chapterResources]);

  // 保存资源配置
  const handleSaveResourceConfig = useCallback(async (chapterId: string) => {
    if (!chapterId || !projectId || !chapterResources[chapterId]) {
      showToast('error', '保存失败', '缺少必要信息');
      return;
    }
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const resource = chapterResources[chapterId];
      
      // 保存所有配置到后端
      await axios.put(
        `${API_BASE_URL}/api/projects/${projectId}/chapter-resources/${chapterId}`,
        {
          use_project_overview: resource.use_project_overview,
          use_tech_requirements: resource.use_tech_requirements,
          use_disqualification: resource.use_disqualification,
          use_quotation: resource.use_quotation,
          use_templates: resource.use_templates,
          use_tender_document: resource.use_tender_document,
          use_other_materials: resource.use_other_materials,
          selected_other_materials: resource.selected_other_materials,
          selected_model: resource.selected_model,
          generation_preferences: resource.generation_preferences
        }
      );
      
      showToast('success', '保存成功', '资源配置已保存');
      // 关闭资源面板
      setActiveResourceChapter(null);
    } catch (error) {
      showToast('error', '保存失败', '请重试');
    }
  }, [chapterResources, projectId, showToast]);

  // 保存资源面板中的目标字数
  const handleSaveResourceWordCount = useCallback(async (item: OutlineItem) => {
    const newWordCount = parseInt(tempResourceWordCount) || 0;
    
    if (newWordCount === item.expected_word_count) {
      setEditingResourceWordCount(null);
      return;
    }
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      
      // 使用正确的API路径
      await axios.post(`${API_BASE_URL}/api/outline/projects/${projectId}/outline/update-word-count`, {
        outline_item_id: item.id,
        expected_word_count: newWordCount
      });
      
      setEditingResourceWordCount(null);
      showToast('success', '目标字数已更新');
      
      // 刷新目录数据以获取最新的字数配置
      if (onRefreshOutline && projectId) {
        onRefreshOutline(projectId);
      }
    } catch (error) {
      showToast('error', '保存字数失败，请重试');
    }
  }, [tempResourceWordCount, projectId, showToast, onRefreshOutline]);

  // 获取章节的上级章节信息
  const getParentChapters = useCallback((targetId: string, items: OutlineItem[], parents: OutlineItem[] = []): OutlineItem[] => {
    for (const item of items) {
      if (item.id === targetId) {
        return parents;
      }
      if (item.children && item.children.length > 0) {
        const found = getParentChapters(targetId, item.children, [...parents, item]);
        if (found.length > 0 || item.children.some(child => child.id === targetId)) {
          return found.length > 0 ? found : [...parents, item];
        }
      }
    }
    return [];
  }, []);

  // 获取章节的同级章节信息
  const getSiblingChapters = useCallback((targetId: string, items: OutlineItem[]): OutlineItem[] => {
    // 直接在当前级别查找
    if (items.some(item => item.id === targetId)) {
      return items;
    }
    
    // 递归在子级别查找
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        const siblings = getSiblingChapters(targetId, item.children);
        if (siblings.length > 0) {
          return siblings;
        }
      }
    }
    
    return [];
  }, []);

  // 从数据库加载章节内容（用于准确统计字数）
  const loadChapterContentsFromDB = useCallback(async (leaves: OutlineItem[]) => {
    if (!projectId || leaves.length === 0) return leaves;
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await axios.get(
        `${API_BASE_URL}/api/projects/${projectId}/chapters`
      );
      
      if (response.data && response.data.chapters) {
        const chapters = response.data.chapters;
        // 创建章节ID到内容的映射
        const chapterMap = new Map(chapters.map((ch: any) => [ch.outline_item_id, ch.content]));
        
        // 更新叶子节点内容（优先使用数据库内容）
        return leaves.map(leaf => {
          const dbContent = chapterMap.get(leaf.id);
          if (dbContent !== undefined && dbContent !== null) {
            return { ...leaf, content: String(dbContent) };
          }
          return leaf;
        });
      }
    } catch (error) {
      console.error('加载章节内容失败:', error);
    }
    return leaves;
  }, [projectId]);

  // 计算目录最大层级
  const calculateMaxLevel = useCallback((outline: OutlineItem[]): number => {
    let maxLevel = 0;
    const traverse = (items: OutlineItem[], level: number) => {
      maxLevel = Math.max(maxLevel, level);
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          traverse(item.children, level + 1);
        }
      });
    };
    traverse(outline, 1);
    return Math.min(maxLevel, 6); // 最多支持6级
  }, []);

  useEffect(() => {
    if (outlineData) {
      const leaves = collectLeafItems(outlineData.outline);
      
      // 先显示本地缓存（快速响应），然后从数据库加载准确内容
      const filtered = draftStorage.filterContentByOutlineLeaves(outlineData.outline);
      const mergedLeaves = leaves.map((leaf) => {
        const cached = filtered[leaf.id];
        return cached ? { ...leaf, content: cached } : leaf;
      });

      // 目录变更时，顺手清理掉无效的旧缓存（只保留当前叶子节点）
      draftStorage.saveContentById(filtered);

      // 使用 startTransition 包装状态更新，避免 flushSync 警告
      startTransition(() => {
        setLeafItems(mergedLeaves);
        setProgress(prev => ({ ...prev, total: leaves.length }));
      });
      
      // 异步从数据库加载准确内容（用于字数统计）
      loadChapterContentsFromDB(mergedLeaves).then(updatedLeaves => {
        setLeafItems(updatedLeaves);
      });
    }
  }, [outlineData, collectLeafItems, loadChapterContentsFromDB]);

  // 加载所有章节的批注
  const loadAllAnnotations = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await annotationApi.getByProject(projectId);
      setAllAnnotations(response.data || []);
    } catch (err) {
      // 加载失败不影响页面显示
    }
  }, [projectId]);

  useEffect(() => {
    loadAllAnnotations();
  }, [loadAllAnnotations]);
  
  // 加载项目信息和公司信息（获取营业执照）
  useEffect(() => {
    const loadProjectAndCompany = async () => {
      if (!projectId) return;
      
      try {
        // 加载项目信息（使用封装好的projectApi，自动带token）
        const projectResponse = await projectApi.getProject(projectId);
        const project = projectResponse.data;
        
        if (project && project.company_credit_code) {
          // 加载公司信息（使用封装好的 companyApi）
          // 注意：这里需要通过统一社会信用代码查询，但 API 不支持
          // 暂时先不处理，或者可以遍历公司列表查找
          const companiesResponse = await companyApi.getCompanies();
          const company = companiesResponse.data.find(
            (c: any) => c.unified_social_credit_code === project.company_credit_code
          );
                  
          if (company && company.business_license_url) {
            setBusinessLicenseUrl(company.business_license_url);
          }
        }
      } catch (err) {
        console.error('加载公司信息失败:', err);
      }
    };
    
    loadProjectAndCompany();
  }, [projectId]);

  // 点击批注导航跳转到对应位置
  const scrollToAnnotationMark = (annotationId: number) => {
    const mark = document.querySelector(`[data-annotation-id="${annotationId}"]`);
    if (mark) {
      mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // 闪烁效果
      mark.classList.add('annotation-flash');
      setTimeout(() => mark.classList.remove('annotation-flash'), 1000);
    }
  };

  // 监听页面滚动，控制回到顶部按钮的显示
  useEffect(() => {
    const scrollContainer = document.getElementById('content-scroll');

    const handleScroll = () => {
      const scrollTop = scrollContainer
        ? scrollContainer.scrollTop
        : (window.pageYOffset || document.documentElement.scrollTop);
      setShowScrollToTop(scrollTop > 300);
    };

    // 初始化计算一次，避免刷新后位置不对
    handleScroll();

    const target: any = scrollContainer || window;
    target.addEventListener('scroll', handleScroll);
    return () => target.removeEventListener('scroll', handleScroll);
  }, []);

  // 加载项目文件
  useEffect(() => {
    const loadProjectFiles = async () => {
      if (!projectId) return;
      
      try {
        const response = await documentApi.getProjectFiles(projectId);
        if (response.data && response.data.files) {
          const files = response.data.files;
          const tender = files.find((f: any) => f.file_category === 'tender');
          const others = files.filter((f: any) => f.file_category === 'other');
          setProjectFiles({ tender, others });
        }
      } catch (error) {
        console.error('加载项目文件失败:', error);
      }
    };
    
    loadProjectFiles();
  }, [projectId]);

  // 加载可用文本模型列表
  useEffect(() => {
    const loadAvailableModels = async () => {
      try {
        const response = await configApi.getModels();
        if (response.data.success && response.data.models) {
          setAvailableTextModels(response.data.models);
        }
      } catch (error) {
        console.error('加载模型列表失败:', error);
      }
    };
    
    loadAvailableModels();
  }, []); // 只在组件挂载时加载一次

  // 回到顶部
  const scrollToTop = () => {
    const scrollContainer = document.getElementById('content-scroll');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // 处理补充材料上传
  const handleSupplementFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !projectId || !activeResourceChapter) {
      console.log('上传条件不满足:', { files: !!files, filesLength: files?.length, projectId, activeResourceChapter });
      return;
    }
    
    console.log('开始上传补充材料:', { projectId, activeResourceChapter, fileCount: files.length });
    setUploadingSupplement(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`上传文件 ${i + 1}/${files.length}:`, file.name, file.size);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('usage_note', '');
        
        console.log('发送POST请求到:', `${API_BASE_URL}/api/projects/${projectId}/chapter-resources/${encodeURIComponent(activeResourceChapter)}/supplement-files`);
        const response = await axios.post(
          `${API_BASE_URL}/api/projects/${projectId}/chapter-resources/${encodeURIComponent(activeResourceChapter)}/supplement-files`,
          formData,
          { 
            // 不设置 Content-Type，让浏览器自动设置（包含 boundary）
            timeout: 300000 // 5分钟超时（大文件上传需要更长时间）
          }
        );
        
        console.log('上传响应:', response.data);
        if (response.data.success) {
          // 重新加载该章节的补充材料列表
          await loadSupplementFiles(activeResourceChapter);
        }
      }
    } catch (error: any) {
      console.error('上传补充材料失败:', error);
      if (error.code === 'ECONNABORTED') {
        alert('上传超时，请检查网络连接后重试');
      } else if (error.response) {
        alert(`上传失败: ${error.response.data?.detail || error.message}`);
      } else {
        alert(`上传失败: ${error.message}`);
      }
    } finally {
      setUploadingSupplement(false);
    }
  };

  // 删除补充材料
  const handleDeleteSupplementFile = async (fileId: string) => {
    if (!projectId || !activeResourceChapter) return;
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      const response = await axios.delete(
        `${API_BASE_URL}/api/projects/${projectId}/chapter-resources/${activeResourceChapter}/supplement-files/${fileId}`
      );
      
      if (response.data.success) {
        // 重新加载列表
        await loadSupplementFiles(activeResourceChapter);
      }
    } catch (error) {
      console.error('删除补充材料失败:', error);
    }
  };

  // 更新补充材料用途说明
  const handleUpdateSupplementUsage = async (fileId: string, usage_note: string) => {
    if (!projectId || !activeResourceChapter) return;
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
      await axios.put(
        `${API_BASE_URL}/api/projects/${projectId}/chapter-resources/${activeResourceChapter}/supplement-files/${fileId}`,
        { usage_note }
      );
    } catch (error) {
      console.error('更新用途说明失败:', error);
    }
  };

  // 点击上传区域
  const handleSupplementUploadClick = () => {
    supplementFileInputRef.current?.click();
  };

  // 拖拽上传
  const handleSupplementDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSupplementDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSupplementFileUpload(e.dataTransfer.files);
  };

  // 获取叶子节点的实时内容
  const getLeafItemContent = (itemId: string): string | undefined => {
    const leafItem = leafItems.find(leaf => leaf.id === itemId);
    return leafItem?.content;
  };

  // 计算内容的实际字数（去除Markdown标记和空白）
  const calculateActualWordCount = (content: string): number => {
    if (!content) return 0;
    
    // 移除Markdown标记
    let text = content
      .replace(/```[\s\S]*?```/g, '') // 代码块
      .replace(/`[^`]+`/g, '') // 行内代码
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // 链接
      .replace(/[#*_~`]/g, '') // 其他Markdown符号
      .replace(/\s+/g, '') // 所有空白字符
      .trim();
    
    return text.length;
  };

  // 获取叶子节点的错误信息
  const getLeafItemError = (itemId: string): string | undefined => {
    const leafItem = leafItems.find(leaf => leaf.id === itemId);
    return leafItem?.error;
  };

  // 递归更新outline中的某个节点
  const updateOutlineItem = (items: OutlineItem[], itemId: string, updates: Partial<OutlineItem>): OutlineItem[] => {
    return items.map(item => {
      if (item.id === itemId) {
        return { ...item, ...updates };
      }
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: updateOutlineItem(item.children, itemId, updates)
        };
      }
      return item;
    });
  };

  // 检查是否为叶子节点
  const isLeafNode = (item: OutlineItem): boolean => {
    return !item.children || item.children.length === 0;
  };

  // 滚动监听 - 智能显示/隐藏顶部面板 + 自动高亮当前章节
  useEffect(() => {
    const scrollContainer = document.getElementById('content-scroll');
    if (!scrollContainer) return;

    const handleScroll = () => {
      const currentScrollY = scrollContainer.scrollTop;
      const lastY = lastScrollYRef.current;
      
      // 1. 控制顶部面板显示/隐藏（编辑模式下始终隐藏）
      if (editingChapter) {
        // 编辑模式下，始终隐藏顶部面板
        setShowTopBar(false);
        lastScrollYRef.current = currentScrollY;
      } else {
        // 非编辑模式，根据滚动方向控制
        if (currentScrollY <= 10) {
          setShowTopBar(true);
          lastScrollYRef.current = currentScrollY;
        } 
        else if (currentScrollY > lastY && currentScrollY - lastY > 50 && currentScrollY > 100) {
          setShowTopBar(false);
          lastScrollYRef.current = currentScrollY;
        } 
        else if (currentScrollY < lastY && lastY - currentScrollY > 10) {
          setShowTopBar(true);
          lastScrollYRef.current = currentScrollY;
        }
      }

      // 2. 检测当前可见的章节（防抖 + 跳过手动滚动）
      // 如果是手动点击滚动，不更新高亮
      if (isManualScrollingRef.current) {
        return;
      }
      
      // 清除之前的防抖定时器
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // 防抖：200ms 后再检测章节
      scrollTimeoutRef.current = setTimeout(() => {
        const chapterElements = scrollContainer.querySelectorAll('[data-chapter-id]');
        let currentChapterId = '';
        
        // 遍历所有章节，找到当前可见的章节
        chapterElements.forEach((element) => {
          const rect = element.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          
          // 如果章节在视窗上方 200px 处，认为是当前章节
          if (rect.top <= containerRect.top + 200 && rect.bottom > containerRect.top) {
            currentChapterId = element.getAttribute('data-chapter-id') || '';
          }
        });
        
        if (currentChapterId && currentChapterId !== activeChapterId) {
          setActiveChapterId(currentChapterId);
          setActiveSection(''); // 清除手动点击的高亮状态
          
          // 滚动左侧目录到可见区域
          setTimeout(() => {
            const activeNavItem = document.querySelector(`[data-nav-id="${currentChapterId}"]`);
            if (activeNavItem) {
              activeNavItem.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'nearest' 
              });
            }
          }, 100);
        }
      }, 200); // 200ms 防抖
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    // 初始化时执行一次
    handleScroll();
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [activeChapterId, editingChapter]); // 添加 editingChapter 依赖

  // 渲染目录结构
    // 替换内容中的编号为与标题一致的编号
    const replaceContentNumbering = (content: string, title: string): string => {
      if (!content) return content;
      
      // 提取标题中的编号前缀
      const titleNumberMatch = title.match(/^(第[一二三四五六七八九十百]+[章节条][、，,\s]+|[一二三四五六七八九十]+[、，,]|（[一二三四五六七八九十]+）[、，,\s]*|\d+(?:\.\d+)*\.?\s*|（\d+）[、，,\s]*)/);
      
      if (!titleNumberMatch) {
        // 标题没有编号，移除内容中的编号
        return removeNumberingFromContent(content);
      }
      
      const titleNumber = titleNumberMatch[0];
      
      // 移除内容开头的旧编号，并添加新编号
      let cleanedContent = removeNumberingFromContent(content);
      
      // 如果内容不以章节标题开头，不添加编号
      // 否则添加与标题一致的编号
      const titleWithoutNumber = removeNumberingFromTitle(title);
      
      // 检查清理后的内容是否以标题开头
      if (cleanedContent.trim().startsWith(titleWithoutNumber)) {
        // 在标题前添加编号
        cleanedContent = cleanedContent.replace(titleWithoutNumber, titleNumber + titleWithoutNumber);
      }
      
      return cleanedContent;
    };
    
    // 移除内容开头的编号
    const removeNumberingFromContent = (content: string): string => {
      if (!content) return content;
      
      // 匹配并移除内容开头的编号（可能在##后面）
      let result = content;
      
      // 匹配 markdown 标题后的编号：## 3.1 授权委托书正文
      result = result.replace(/^(#{1,6}\s*)(\d+(?:\.\d+)*\.?\s*)/gm, '$1');
      
      // 匹配直接开头的编号：3.1 授权委托书正文
      result = result.replace(/^\d+(?:\.\d+)*\.?\s*/gm, '');
      
      return result;
    };
  
  const renderOutline = (items: OutlineItem[], level: number = 1): React.ReactElement[] => {
    return items.map((item) => {
      const isLeaf = isLeafNode(item);
      let currentContent = isLeaf ? getLeafItemContent(item.id) : item.content;
      
      // 如果是叶子节点且有内容，移除内容开头可能重复的标题
      if (isLeaf && currentContent) {
        // 移除内容开头的标题（可能是Markdown标题格式）
        const titleWithoutNumbering = removeNumberingFromTitle(item.title);
        
        // 转义特殊字符
        const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        // 尝试匹配并移除内容开头的标题（支持多种格式）
        // #+\s* 匹配任意数量的#号（1个或多个），支持所有层级
        const titlePatterns = [
          // Markdown 标题格式（任意层级 #、##、###、####...）+ 完整标题
          new RegExp(`^\\s*#+\\s*${escapeRegex(item.title)}(?:\\s*\n)*`, 'i'),
          // Markdown 标题 + 去掉编号末尾的点号（7.4. 项目管理 → 7.4 项目管理）
          new RegExp(`^\\s*#+\\s*${escapeRegex(item.title.replace(/^([\d\.]+)\.\s*/, '$1 '))}(?:\\s*\n)*`, 'i'),
          // Markdown 标题 + 去掉所有点号的版本
          new RegExp(`^\\s*#+\\s*${escapeRegex(item.title.replace(/\./g, ''))}(?:\\s*\n)*`, 'i'),
          // Markdown 标题 + 无编号的标题
          new RegExp(`^\\s*#+\\s*${escapeRegex(titleWithoutNumbering)}(?:\\s*\n)*`, 'i'),
          // 粗体标题（AI 生成内容中的子标题格式）：**3.1 项目背景**
          new RegExp(`^\\s*\\*\\*${escapeRegex(item.title)}\\*\\*(?:\\s*\n)*`, 'i'),
          // 粗体标题 + 去掉编号末尾的点号（3.1. 标题 → 3.1 标题）
          new RegExp(`^\\s*\\*\\*${escapeRegex(item.title.replace(/^([\d\.]+)\.\s*/, '$1 '))}\\*\\*(?:\\s*\n)*`, 'i'),
          // 粗体标题 + 去掉所有点号
          new RegExp(`^\\s*\\*\\*${escapeRegex(item.title.replace(/\./g, ''))}\\*\\*(?:\\s*\n)*`, 'i'),
          // 粗体标题（仅文本，无编号）：**项目背景**
          new RegExp(`^\\s*\\*\\*${escapeRegex(titleWithoutNumbering)}\\*\\*(?:\\s*\n)*`, 'i'),
          // 纯文本标题（完整）
          new RegExp(`^\\s*${escapeRegex(item.title)}(?:\\s*\n)+`, 'i'),
          // 纯文本标题（去掉编号末尾的点号）
          new RegExp(`^\\s*${escapeRegex(item.title.replace(/^([\d\.]+)\.\s*/, '$1 '))}(?:\\s*\n)+`, 'i'),
          // 纯文本标题（去掉所有点号）
          new RegExp(`^\\s*${escapeRegex(item.title.replace(/\./g, ''))}(?:\\s*\n)+`, 'i'),
          // 纯文本标题（无编号）
          new RegExp(`^\\s*${escapeRegex(titleWithoutNumbering)}(?:\\s*\n)+`, 'i'),
        ];
        
        // 尝试移除匹配的标题
        let matched = false;
        for (const pattern of titlePatterns) {
          const newContent: string = currentContent.replace(pattern, '');
          if (newContent !== currentContent) {
            currentContent = newContent.replace(/^\n+/, ''); // 移除开头的空行
            matched = true;
            break;
          }
        }
      }
      
      return (
        <div 
          key={item.id} 
          id={`section-${item.id}`} 
          data-chapter-id={item.id}  // 添加 data 属性用于滚动检测
          className={`mb-${level === 1 ? '8' : '4'} relative`}
        >
          {/* 标题和操作按钮 */}
          <div className="flex items-center justify-between mb-2 gap-3">
            <div 
              className={`text-${level === 1 ? 'xl' : level === 2 ? 'lg' : 'base'} font-${level === 1 ? 'bold' : 'semibold'} text-gray-900 flex-1 min-w-0`}
              title={item.title}
            >
              <span className="truncate block">{item.title}</span>
            </div>
            
            {/* 仅叶子节点显示操作按钮 */}
            {isLeaf && (
              <div className="flex items-center space-x-2">
                {/* 编辑和重写按钮：仅在有内容时显示 */}
                {currentContent && (
                  <>
                    {editingChapter !== item.id && (
                      // 非编辑状态显示编辑按钮
                      <button
                        onClick={() => handleStartEdit(item.id, currentContent || '')}
                        disabled={progress.generating.has(item.id)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-green-600 bg-white hover:bg-green-50 border border-gray-300 hover:border-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={progress.generating.has(item.id) ? '正在生成中，请稍候...' : '双击可进入编辑模式'}
                      >
                        <PencilIcon className="w-4 h-4 mr-1" />
                        编辑
                      </button>
                    )}
                                                
                    {editingChapter !== item.id && (
                      <button
                        onClick={() => handleRewriteChapter(item)}
                        disabled={progress.generating.has(item.id)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 border border-gray-300 hover:border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="重写本章节"
                      >
                        <ArrowPathIcon className="w-4 h-4 mr-1" />
                        重写
                      </button>
                    )}
                  </>
                )}
                            
                {/* 资源按钮：无论是否有内容都显示 */}
                {editingChapter !== item.id && (
                  <button
                    onClick={(e) => handleToggleResource(item, e)}
                    disabled={progress.generating.has(item.id)}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-600 bg-white hover:bg-gray-50 border border-gray-300 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={chaptersWithProcessingFiles.has(item.id) ? "有文件正在处理中..." : "关联生成资源"}
                  >
                    {chaptersWithProcessingFiles.has(item.id) ? (
                      <>
                        {/* 转圈图标 */}
                        <svg className="animate-spin w-4 h-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        资源
                      </>
                    ) : (
                      <>
                        <LinkIcon className="w-4 h-4 mr-1" />
                        资源
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* 描述 */}
          <div className="text-sm text-gray-600 mb-4">
            {item.description}
          </div>

          {/* 内容（仅叶子节点） */}
          {isLeaf && (
            <div className="border-l-4 border-blue-200 pl-4 mb-6">
              {currentContent ? (
                <div className={`prose max-w-none ${styleConfig.paragraphIndent !== false ? 'paragraph-indent' : ''}`}>
                  {editingChapter === item.id ? (
                    // 编辑模式 - 统一的 Tiptap 编辑器
                    <UnifiedEditor
                      key={`edit-${item.id}`}
                      content={editContent}
                      editable={true}
                      selectedModel={selectedModel}
                      businessLicenseUrl={businessLicenseUrl}
                      onChange={(markdown) => handleContentChange(markdown, item.id)}
                      onExit={() => handleCancelEdit()}
                    />
                  ) : (
                    // 预览模式 - 带注记功能
                    <div 
                      className={`rounded p-2 -m-2 transition-all relative group ${
                        progress.generating.has(item.id) 
                          ? 'cursor-not-allowed opacity-60' 
                          : 'cursor-text'
                      }`}
                      onDoubleClick={() => {
                        // 生成中不允许编辑
                        if (!progress.generating.has(item.id)) {
                          handleStartEdit(item.id, currentContent || '');
                        }
                      }}
                      title={progress.generating.has(item.id) ? '正在生成中，请稍候...' : '双击编辑内容'}
                    >
                      {!progress.generating.has(item.id) && (
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                          <span className="text-xs text-gray-400 bg-white px-2 py-1 rounded shadow-sm border border-gray-200">
                            双击编辑
                          </span>
                        </div>
                      )}
                      {projectId ? (
                        <AnnotatedContent
                          key={`preview-${item.id}`}
                          content={replaceContentNumbering(currentContent, item.title)}
                          outlineNodeId={hashCode(item.id)}
                          projectId={projectId}
                          onContentChange={(newContent) => handleAnnotationContentChange(item.id, newContent)}
                          isActive={activeChapterId === item.id}
                          onAnnotationsChange={loadAllAnnotations}  // 批注变更时刷新全局列表
                          allAnnotations={allAnnotations}  // 传入所有批注
                          showAnnotationPanel={showAnnotationPanel}
                          onToggleAnnotationPanel={(show) => {
                            setShowAnnotationPanel(show);
                            // 展开批注面板时关闭所有资源面板
                            if (show) {
                              setActiveResourceChapter(null);
                            }
                          }}
                        />
                      ) : (
                        <UnifiedEditor
                          key={`preview-${item.id}`}
                          content={replaceContentNumbering(currentContent, item.title)}
                          editable={false}
                        />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-4">
                  {progress.generating.has(item.id) ? (
                    <div className="text-blue-600 italic flex items-center">
                      <svg className="w-4 h-4 mr-2 animate-spin" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="#e5e7eb" strokeWidth="2" fill="none" />
                        <path d="M 8 2 A 6 6 0 0 1 14 8" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" fill="none" />
                      </svg>
                      正在生成内容...
                    </div>
                  ) : (
                    <button
                      onClick={() => handleGenerateSingleChapter(item)}
                      disabled={progress.generating.size >= 5}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 hover:border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={progress.generating.size >= 5 ? '最多同时生成5个章节，请等待当前任务完成' : '点击生成本章节内容'}
                    >
                      <PlayIcon className="w-4 h-4 mr-2" />
                      AI生成本章节内容
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 子章节 */}
          {item.children && item.children.length > 0 && (
            <div className={`ml-${level * 4} mt-4`}>
              {renderOutline(item.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // 获取节点的生成状态
  const getNodeStatus = (item: OutlineItem): 'empty' | 'partial' | 'complete' | 'generating' | 'failed' | 'interrupted' => {
    // 如果是叶子节点,直接检查内容和生成状态
    if (!item.children || item.children.length === 0) {
      // 检查是否被中断
      const leafItem = leafItems.find(leaf => leaf.id === item.id);
      if (leafItem?.interrupted || item.interrupted) {
        return 'interrupted';
      }
      
      // 优先检查leafItems中的错误状态(最新状态)
      const leafError = getLeafItemError(item.id);
      if (leafError || item.error) {
        return 'failed';
      }
      // 检查是否正在生成
      if (progress.generating.has(item.id)) {
        return 'generating';
      }
      const leafContent = getLeafItemContent(item.id);
        
      // 检查内容是否为空
      if (!leafContent || leafContent.trim() === '') {
        return 'empty';
      }
        
      // 检查内容是否为错误信息(以"错误:"或"Error:"开头)
      const trimmedContent = leafContent.trim();
      if (trimmedContent.startsWith('错误:') || trimmedContent.startsWith('Error:')) {
        return 'failed';
      }
        
      return 'complete';
    }
    
    // 如果有子节点，递归检查所有子节点的状态
    const childStatuses = item.children.map(child => getNodeStatus(child));
    const hasGenerating = childStatuses.includes('generating');
    const hasFailed = childStatuses.includes('failed');
    const hasInterrupted = childStatuses.includes('interrupted'); // ✅ 新增：检查中断状态
    const hasEmpty = childStatuses.includes('empty');
    const hasComplete = childStatuses.includes('complete') || childStatuses.includes('partial');
    
    // ✅ 如果有子节点被中断，父节点显示为部分完成状态
    if (hasInterrupted) {
      return 'partial';
    }
    
    // 如果有子节点失败，父节点显示为部分失败状态
    if (hasFailed && (hasComplete || hasGenerating)) {
      return 'partial';
    }
    // 如果全部失败
    if (hasFailed && !hasComplete && !hasGenerating && !hasEmpty) {
      return 'failed';
    }
    
    // 如果有子节点正在生成，父节点显示为部分完成状态
    if (hasGenerating) {
      return 'partial';
    }
    
    if (!hasComplete) {
      return 'empty'; // 全部为空
    } else if (hasEmpty || hasFailed) {
      return 'partial'; // 部分完成或有失败
    } else {
      return 'complete'; // 全部完成
    }
  };

  // 渲染状态指示器
  const renderStatusIndicator = (status: 'empty' | 'partial' | 'complete' | 'generating' | 'failed' | 'interrupted') => {
    if (status === 'interrupted') {
      // 黄色感叹号（生成被中断）
      return (
        <svg className="w-4 h-4 mr-2 flex-shrink-0" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" fill="#f59e0b" />
          <path d="M8 4v5M8 11v1" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    } else if (status === 'failed') {
      // 红色感叹号（生成失败）
      return (
        <svg className="w-4 h-4 mr-2 flex-shrink-0" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" fill="#ef4444" />
          <path d="M8 4v5M8 11v1" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    } else if (status === 'complete') {
      // 绿色实心带对勾
      return (
        <svg className="w-4 h-4 mr-2 flex-shrink-0" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" fill="#10b981" />
          <path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    } else if (status === 'generating') {
      // 转圈等待动画（生成中）
      return (
        <svg className="w-4 h-4 mr-2 flex-shrink-0 animate-spin" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6" stroke="#e5e7eb" strokeWidth="2" fill="none" />
          <path d="M 8 2 A 6 6 0 0 1 14 8" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" fill="none" />
        </svg>
      );
    } else if (status === 'partial') {
      // 半蓝色圈（一半蓝色背景一半空心）
      return (
        <svg className="w-4 h-4 mr-2 flex-shrink-0" viewBox="0 0 16 16" fill="none">
          <defs>
            <clipPath id="half-clip">
              <rect x="0" y="0" width="8" height="16" />
            </clipPath>
          </defs>
          <circle cx="8" cy="8" r="7" stroke="#3b82f6" strokeWidth="1.5" fill="none" />
          <circle cx="8" cy="8" r="7" fill="#3b82f6" clipPath="url(#half-clip)" />
        </svg>
      );
    } else {
      // 空心蓝色圈
      return (
        <svg className="w-4 h-4 mr-2 flex-shrink-0" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="#3b82f6" strokeWidth="1.5" fill="none" />
        </svg>
      );
    }
  };

  // 渲染左侧导航目录
  const renderOutlineNav = (items: OutlineItem[], level: number = 0): React.ReactElement[] => {
    return items.map((item) => {
      // 优先使用滚动检测的 activeChapterId，其次才是手动点击的 activeSection
      const isActive = activeChapterId ? 
        (activeChapterId === item.id) : 
        (activeSection === item.id);
      const hasChildren = item.children && item.children.length > 0;
      const fullTitle = item.title;
      const status = getNodeStatus(item);
      
      return (
        <div key={item.id}>
          <button
            onClick={() => scrollToSection(item.id)}
            data-nav-id={item.id}  // 添加 data 属性用于滚动定位
            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
              isActive 
                ? 'bg-blue-100 text-blue-700 font-medium' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            style={{ paddingLeft: `${(level + 1) * 12}px` }}
            title={fullTitle}
          >
            <div className="flex items-center overflow-hidden">
              {renderStatusIndicator(status)}
              <span className="truncate">{item.title}</span>
            </div>
          </button>
          {hasChildren && (
            <div className="mt-1">
              {renderOutlineNav(item.children!, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // 滚动到指定章节
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(`section-${sectionId}`);
    const scrollContainer = document.getElementById('content-scroll');
    
    // 标记为手动滚动，阻止自动检浊
    isManualScrollingRef.current = true;
    
    if (element && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop;
      const targetPosition = scrollTop + elementRect.top - containerRect.top - 80; // 80px offset for header
      
      scrollContainer.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
      
      // 点击时，暂时使用 activeSection，清除自动检测的 activeChapterId
      setActiveSection(sectionId);
      setActiveChapterId(''); // 清除自动检测的高亮，让手动点击生效
      
      // 1秒后解除手动滚动标记，恢复自动检测
      setTimeout(() => {
        isManualScrollingRef.current = false;
      }, 1000);
    } else if (element) {
      // 如果找不到滚动容器，使用元素自身的滚动
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
      setActiveChapterId('');
      
      setTimeout(() => {
        isManualScrollingRef.current = false;
      }, 1000);
    }
  };

  // 开始编辑章节内容
  const handleStartEdit = (chapterId: string, content: string) => {
    setEditingChapter(chapterId);
    setEditContent(content);
    setShowTopBar(false);  // 隐藏顶部面板
  };

  // 内容变化处理 - 带防抖的自动保存
  const handleContentChange = (content: string, chapterId: string) => {
    setEditContent(content);
    
    // 清除之前的定时器
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }
    
    // 设置新的定时器，1秒后自动保存
    const timer = setTimeout(() => {
      handleAutoSave(chapterId, content);
    }, 1000);
    
    setAutoSaveTimer(timer);
  };

  // 自动保存
  const handleAutoSave = (chapterId: string, content?: string) => {
    const contentToSave = content !== undefined ? content : editContent;
    
    // 更新leafItems，同时清除 interrupted 和 error 状态（手动编辑后认为已修复）
    setLeafItems(prevItems => {
      const newItems = [...prevItems];
      const index = newItems.findIndex(i => i.id === chapterId);
      if (index !== -1) {
        newItems[index] = { 
          ...newItems[index], 
          content: contentToSave,
          interrupted: undefined, // ✅ 清除中断状态
          error: undefined        // ✅ 清除错误状态
        };
      }
      return newItems;
    });

    // 保存到本地缓存
    draftStorage.upsertChapterContent(chapterId, contentToSave);
    
    // 防抖保存到数据库（如果提供了回调）
    if (onChapterSave) {
      onChapterSave(chapterId, contentToSave);
    }
  };

  // 取消编辑
  const handleCancelEdit = () => {
    // 清除自动保存定时器
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      setAutoSaveTimer(null);
    }
    
    setEditingChapter(null);
    setEditContent('');
    setShowTopBar(true);  // 显示顶部面板
  };

  // 处理注记内容变更
  const handleAnnotationContentChange = (chapterId: string, newContent: string) => {
    // 更新leafItems，同时清除 interrupted 和 error 状态（手动编辑后认为已修复）
    setLeafItems(prevItems => {
      const newItems = [...prevItems];
      const index = newItems.findIndex(i => i.id === chapterId);
      if (index !== -1) {
        newItems[index] = { 
          ...newItems[index], 
          content: newContent,
          interrupted: undefined, // ✅ 清除中断状态
          error: undefined        // ✅ 清除错误状态
        };
      }
      return newItems;
    });

    // 保存到本地缓存
    draftStorage.upsertChapterContent(chapterId, newContent);
    
    // 保存到数据库
    if (onChapterSave) {
      onChapterSave(chapterId, newContent);
    }
  };

  // 保存编辑
  const handleSaveEdit = (chapterId: string) => {
    // 立即保存当前编辑内容
    handleAutoSave(chapterId);
    
    // 退出编辑模式
    handleCancelEdit();
  };

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [autoSaveTimer]);
  
  // Ctrl+S 快捷键：保存并退出编辑模式
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S 或 Cmd+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault(); // 阻止浏览器默认保存行为
        
        if (editingChapter) {
          // 如果正在编辑，保存并退出
          handleSaveEdit(editingChapter);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingChapter, editContent]); // 依赖 editingChapter 和 editContent

  // 监听 Token 欠费警告后的"继续生成"事件
  useEffect(() => {
    const handleTokenContinue = () => {
      // 设置强制继续标志
      forceOverdraftRef.current = true;
      
      // 如果有待重试的章节，重新生成
      if (pendingOverdraftChapterRef.current) {
        const item = pendingOverdraftChapterRef.current;
        pendingOverdraftChapterRef.current = null;
        // 延迟执行，确保弹框已关闭
        setTimeout(() => {
          handleGenerateSingleChapter(item);
        }, 100);
      }
    };

    window.addEventListener('token:continue', handleTokenContinue as EventListener);
    return () => window.removeEventListener('token:continue', handleTokenContinue as EventListener);
  }, []);

  // 重写章节内容
  const handleRewriteChapter = async (item: OutlineItem) => {
    if (!outlineData || isGenerating) return;

    // 确认是否重写
    if (item.content && item.content.trim() !== '') {
      const confirmed = window.confirm(`确定要重写"${item.title}"的内容吗？当前内容将被覆盖。`);
      if (!confirmed) return;
    }

    // 清空当前章节内容
    setLeafItems(prevItems => {
      const newItems = [...prevItems];
      const index = newItems.findIndex(i => i.id === item.id);
      if (index !== -1) {
        newItems[index] = { ...newItems[index], content: '' };
      }
      return newItems;
    });

    // 清空本地缓存
    draftStorage.upsertChapterContent(item.id, '');

    // 重新生成
    await handleGenerateSingleChapter(item);
  };

  // 查看生成历史
  const handleShowHistory = (item: OutlineItem) => {
    // TODO: 实现生成历史功能
    alert(`查看"${item.title}"的生成历史功能开发中...`);
  };
    
  // 切换资源面板
  const handleToggleResource = async (item: OutlineItem, event?: React.MouseEvent<HTMLButtonElement>) => {
    if (activeResourceChapter === item.id) {
      // 关闭当前资源面板
      setActiveResourceChapter(null);
      setActiveResourceChapterData(null);
    } else {
      // 打开新的资源面板，关闭批注面板
      setActiveResourceChapter(item.id);
      setActiveResourceChapterData(item);
      setShowAnnotationPanel(false);
        
      // 如果该章节还没有资源配置，自动创建一个（使用智能匹配）
      if (!chapterResources[item.id] && outlineData) {
        try {
          console.log('章节资源不存在，执行智能匹配...');
          const { matchChapterResources } = await import('../utils/resourceMatcher');
          const chapterInfo = {
            id: item.id,
            title: item.title,
            description: item.description || ''
          };
          const suggestion = matchChapterResources(chapterInfo);
            
          // 立即更新UI
          setChapterResources(prev => ({
            ...prev,
            [item.id]: {
              use_project_overview: suggestion.use_project_overview,
              use_tech_requirements: suggestion.use_tech_requirements,
              use_disqualification: suggestion.use_disqualification,
              use_quotation: suggestion.use_quotation,
              use_templates: suggestion.use_templates,
              use_tender_document: false,
              use_other_materials: false,
              selected_other_materials: [],
              selected_model: undefined,
              generation_preferences: {},
              suggested: {
                tech_requirements: suggestion.is_tech_requirements_suggested,
                quotation: suggestion.is_quotation_suggested,
                templates: suggestion.is_templates_suggested
              }
            }
          }));
            
          // 保存到数据库
          const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8000';
          await axios.post(
            `${API_BASE_URL}/api/projects/${projectId}/chapter-resources/batch-init`,
            { 
              suggestions: {
                [item.id]: suggestion
              }
            }
          );
            
          console.log('章节资源已自动创建:', suggestion);
        } catch (error) {
          console.error('自动创建章节资源失败:', error);
        }
      }
    }
  };
    
  // 切换资源选项
  // 旧的handleToggleResourceOption已被移到上面的useCallback中
  
  // 生成单个章节的内容
  const handleGenerateSingleChapter = async (item: OutlineItem) => {
    if (!outlineData) return;
    
    // 检查并发数量限制
    if (progress.generating.size >= 5) {
      showToast('warning', '最多同时生成5个章节，请等待当前任务完成');
      return;
    }

    // 从outlineData中提取项目概述
    const projectOverview = outlineData.project_overview || '';

    // 生成时关闭资源面板
    setActiveResourceChapter(null);
    setActiveResourceChapterData(null);

    // 删除该章节的所有批注（重新生成会覆盖原内容）
    try {
      await annotationApi.deleteByNode(hashCode(item.id));
      loadAllAnnotations();  // 刷新全局批注列表
    } catch (err) {
      console.error('删除章节批注失败:', err);
    }

    // 清除该章节的 interrupted 和 error 状态（重新生成时重置状态）
    setLeafItems(prevItems => {
      return prevItems.map(leaf => 
        leaf.id === item.id 
          ? { ...leaf, interrupted: undefined, error: undefined } 
          : leaf
      );
    });

    // 将该章节添加到正在生成的集合中
    setProgress(prev => ({
      ...prev,
      total: Math.max(prev.total, prev.generating.size + 1), // 动态增加总数
      current: item.title,
      generating: new Set<string>([...Array.from(prev.generating), item.id])
    }));

    // 创建 AbortController
    const abortController = new AbortController();
    abortControllersRef.current.set(item.id, abortController);

    try {
      const updatedItem = await generateItemContent(item, projectOverview, abortController);
      
      // 移除 AbortController
      abortControllersRef.current.delete(item.id);
      
      // 更新叶子节点数据
      setLeafItems(prevItems => {
        const newItems = [...prevItems];
        const index = newItems.findIndex(i => i.id === item.id);
        if (index !== -1) {
          newItems[index] = { ...updatedItem };
        }
        return newItems;
      });

      // 更新完成数和移除generating状态
      setProgress(prev => ({ 
        ...prev, 
        completed: prev.completed + 1,
        generating: new Set<string>([...Array.from(prev.generating)].filter(id => id !== item.id))
      }));
    } catch (error) {
      console.error(`生成章节内容失败 ${item.title}:`, error);
      
      // 移除 AbortController
      abortControllersRef.current.delete(item.id);
      
      // 移除generating状态，即使失败也要释放并发槽位
      setProgress(prev => ({
        ...prev,
        failed: [...prev.failed, item.id],
        generating: new Set<string>([...Array.from(prev.generating)].filter(id => id !== item.id))
      }));
    }
  };

  // 生成单个章节内容
    // 移除标题中的所有编号格式
    const removeNumberingFromTitle = (title: string): string => {
      let cleanTitle = title;
      
      // 移除所有可能的编号格式
      const patterns = [
        /^第[一二三四五六七八九十百]+章[、，,\s]+/,
        /^第[一二三四五六七八九十百]+节[、，,\s]+/,
        /^第[一二三四五六七八九十百]+条[、，,\s]+/,
        /^[一二三四五六七八九十]+[、，,]/,
        /^（[一二三四五六七八九十]+）[、，,\s]*/,
        // 统一数字层级编号：匹配 "3.", "3.1", "3.1.", "3.1.1", "3.1.1." 等格式（含可选尾随空格，允许前导空白）
        /^\s*\d+(?:\.\d+)*\.?\s*/,
        /^（\d+）[、，,\s]*/,
      ];
      
      for (const pattern of patterns) {
        cleanTitle = cleanTitle.replace(pattern, '');
      }
      
      return cleanTitle.trim();
    };
  
  const generateItemContent = async (item: OutlineItem, projectOverview: string, abortController: AbortController): Promise<OutlineItem> => {
    if (!outlineData) throw new Error('缺少目录数据');
    
    // 将当前项目添加到正在生成的集合中
    setProgress(prev => ({ 
      ...prev, 
      current: item.title,
      generating: new Set([...Array.from(prev.generating), item.id])
    }));
    
    let updatedItem = { ...item }; // 在函数开始就初始化，以便在catch中访问
    
    try {
      // 移除标题中的编号，只保留纯标题
      const cleanTitle = removeNumberingFromTitle(item.title);
      
      // 获取上级章节和同级章节信息
      const parentChapters = getParentChapters(item.id, outlineData.outline);
      const siblingChapters = getSiblingChapters(item.id, outlineData.outline);
      
      // 也移除父纠和兄弟章节的编号
      const cleanedParentChapters = parentChapters.map(chapter => ({
        ...chapter,
        title: removeNumberingFromTitle(chapter.title)
      }));
      const cleanedSiblingChapters = siblingChapters.map(chapter => ({
        ...chapter,
        title: removeNumberingFromTitle(chapter.title)
      }));

      // 获取章节资源配置中的生成偏向
      const resourceConfig = chapterResources[item.id];
      const generationGuidance = resourceConfig?.generation_preferences?.guidance || '';

      const request: ChapterContentRequest = {
        project_id: projectId || '',  // 添加project_id
        chapter: { 
          ...item, 
          title: cleanTitle,  // 无编号的标题，用于AI提示词
          original_title: item.title,  // 含编号的原始标题（如"3.1. 数据采集"），用于后端推导正确编号
          generation_guidance: generationGuidance  // 添加生成偏向
        },
        parent_chapters: cleanedParentChapters,
        sibling_chapters: cleanedSiblingChapters,
        project_overview: projectOverview,
        selected_model: selectedModel  // 使用项目配置的模型
      };

      const response = await contentApi.generateChapterContentStream(request, abortController.signal, forceOverdraftRef.current);

      if (!response.ok) throw new Error('生成失败');

      const reader = response.body?.getReader();
      if (!reader) throw new Error('无法读取响应');

      let content = '';
      // updatedItem已在函数开始处初始化
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(data);
              
              if (parsed.status === 'streaming' && parsed.full_content) {
                // 实时更新内容
                content = parsed.full_content;
                updatedItem.content = content;
                // 本地持久化（刷新后可恢复）
                draftStorage.upsertChapterContent(item.id, content);
                
                // 使用 requestAnimationFrame 优化渲染性能，避免 flushSync 警告
                requestAnimationFrame(() => {
                  setLeafItems(prevItems => {
                    const newItems = [...prevItems];
                    const index = newItems.findIndex(i => i.id === item.id);
                    if (index !== -1) {
                      newItems[index] = { ...updatedItem };
                    }
                    return newItems;
                  });
                });
              } else if (parsed.status === 'completed' && parsed.content) {
                content = parsed.content;
                updatedItem.content = content;
                // 本地持久化（最终结果）
                draftStorage.upsertChapterContent(item.id, content);
                
                // 保存到数据库
                if (onChapterSave && projectId) {
                  try {
                    onChapterSave(item.id, content);
                    console.log(`✅ 章节 ${item.id} 已保存到数据库`);
                  } catch (error) {
                    // 静默处理保存错误，不影响生成流程
                    console.warn(`⚠️ 保存章节 ${item.id} 到数据库失败（可能章节已被删除或ID不存在）:`, error);
                  }
                }
              } else if (parsed.status === 'error') {
                throw new Error(parsed.message);
              }
            } catch (e) {
              // 忽略JSON解析错误
            }
          }
        }
      }

      return updatedItem;
    } catch (error) {
      // 检查是否是中断错误
      if (error instanceof Error && error.name === 'AbortError') {
        // 中断错误，标记为 interrupted
        updatedItem.interrupted = true;
        
        setLeafItems(prevItems => {
          return prevItems.map(leaf => 
            leaf.id === item.id ? { ...leaf, interrupted: true, error: undefined } : leaf
          );
        });
        
        return updatedItem;
      }
      
      // 其他错误，设置错误信息
      const errorMessage = error instanceof Error ? error.message : '生成失败';
      const isTokenWarning = errorMessage.includes('欠费') && errorMessage.includes('是否继续');
      const isTokenOverdraft = errorMessage.includes('Token') && 
          (errorMessage.includes('超过最大欠费额度') || errorMessage.includes('已达最大欠费额度'));

      if (isTokenWarning) {
        // Token 欠费警告（402）：保存当前章节，等待用户选择是否继续
        console.log('Token 欠费警告，保存章节等待用户选择:', item.title);
        pendingOverdraftChapterRef.current = item;
        updatedItem.interrupted = true;
        setLeafItems(prevItems => {
          return prevItems.map(leaf =>
            leaf.id === item.id ? { ...leaf, interrupted: true, error: undefined } : leaf
          );
        });
      } else if (isTokenOverdraft) {
        // Token 已超过最大欠费额度：已通过事件弹框提示
        console.log('Token 已超过最大欠费额度，跳过章节错误标记');
        updatedItem.interrupted = true;
        setLeafItems(prevItems => {
          return prevItems.map(leaf =>
            leaf.id === item.id ? { ...leaf, interrupted: true, error: undefined } : leaf
          );
        });
      } else {
        updatedItem.error = errorMessage;
        // 更新leafItems，设置error字段
        setLeafItems(prevItems => {
          return prevItems.map(leaf =>
            leaf.id === item.id ? { ...leaf, error: errorMessage } : leaf
          );
        });
        setProgress(prev => ({
          ...prev,
          failed: [...prev.failed, item.title]
        }));
      }
      
      return updatedItem; // 返回带有error的item
    } finally {
      // 从正在生成的集合中移除当前项目
      setProgress(prev => {
        const newGenerating = new Set(Array.from(prev.generating));
        newGenerating.delete(item.id);
        return {
          ...prev,
          generating: newGenerating
        };
      });
      
      // 触发Token刷新事件（无论成功或失败，都可能消耗了Token）
      window.dispatchEvent(new CustomEvent('token:used'));
      console.log('✨ 章节内容生成完成，触发Token刷新事件');
    }
  };

  // 开始生成所有内容
  const handleGenerateContent = async () => {
    if (!outlineData || leafItems.length === 0) return;

    // 检查 Token 余额：如果已欠费（小于0），显示欠费弹窗
    if (tokenBalance !== undefined && tokenBalance !== null && tokenBalance < 0) {
      setTokenAlert({
        show: true,
        type: 'zero',
        title: 'Token 已欠费',
        message: `您的 Token 已欠费 ${Math.abs(tokenBalance).toLocaleString()}，无法使用批量生成功能。请立即充值以恢复使用。`
      });
      return;
    }

    // 检查是否已有生成的内容
    const hasGeneratedContent = leafItems.some(item => item.content && item.content.trim().length > 0);
    
    if (hasGeneratedContent) {
      // 有已生成的内容，询问用户选择（三个按钮：重写全部、继续生成、取消）
      setConfirmDialog({
        isOpen: true,
        title: '选择生成方式',
        message: '检测到已有部分章节生成完成，请选择：\n\n• 重写全部：清空所有内容，重新生成全部章节\n• 继续生成：保留已生成章节，只生成未完成的章节',
        confirmText: '重写全部',
        cancelText: '取消',
        showSecondButton: true,
        secondButtonText: '继续生成',
        secondButtonAction: () => {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          doGenerateContent(false); // false = 只生成未生成的
        },
        onConfirm: () => {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          doGenerateContent(true); // true = 重写全部
        },
        onCancel: () => {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          // 不执行任何生成操作
        }
      });
    } else {
      // 没有已生成的内容，直接生成
      doGenerateContent(true); // 全新生成
    }
  };
  
  // 停止生成
  const handleStopGeneration = () => {
    stopGenerationRef.current = true;
    
    // 立即中断所有正在生成的请求
    abortControllersRef.current.forEach((controller, chapterId) => {
      controller.abort();
    });
    
    // 将正在生成的章节标记为中断状态（黄色警告图标）
    setLeafItems(prevItems => {
      return prevItems.map(item => {
        if (progress.generating.has(item.id)) {
          return { ...item, interrupted: true, error: undefined };
        }
        return item;
      });
    });
    
    // 清空 AbortController Map
    abortControllersRef.current.clear();
    
    showToast('warning', '生成已停止');
  };
  
  // 执行生成内容
  const doGenerateContent = async (clearAll: boolean = true) => {
    if (!outlineData || leafItems.length === 0) return;

    // 重置停止标志
    stopGenerationRef.current = false;

    let itemsToGenerate = leafItems;

    if (clearAll) {
      // 重写全部：清空所有已生成的内容
      draftStorage.clearAllContent();
      
      // 清空 leafItems 的 content，同时清除 interrupted 和 error 状态
      const clearedLeafItems = leafItems.map(item => ({
        ...item,
        content: '',
        interrupted: undefined,
        error: undefined
      }));
      setLeafItems(clearedLeafItems);
      itemsToGenerate = clearedLeafItems;
    } else {
      // 继续生成：只生成未生成的章节
      // ✅ 正确逻辑：黄标(interrupted)、红标(error)、空心蓝圈(无内容) 都需要生成
      // ✅ 只有绿色对勾(有内容且无错误状态) 才算已生成，跳过
      itemsToGenerate = leafItems.filter(item => {
        // 如果有中断或错误标记，需要重新生成
        if (item.interrupted || item.error) {
          return true;
        }
        // 如果内容为空，需要生成
        if (!item.content || item.content.trim().length === 0) {
          return true;
        }
        // 有内容且没有错误状态，算已生成，跳过
        return false;
      });
      
      if (itemsToGenerate.length === 0) {
        showToast('info', '所有章节已生成完成');
        return;
      }
      
      // ✅ 立即清除即将生成的章节的 interrupted 和 error 状态（同步操作）
      itemsToGenerate = itemsToGenerate.map(item => ({
        ...item,
        interrupted: undefined,
        error: undefined
      }));
      
      // 同步更新 leafItems
      setLeafItems(prevItems => {
        return prevItems.map(item => {
          const clearedItem = itemsToGenerate.find(i => i.id === item.id);
          return clearedItem || item;
        });
      });
    }

    setIsGenerating(true);
    setProgress({
      total: itemsToGenerate.length,
      completed: 0,
      current: '',
      failed: [],
      generating: new Set<string>()
    });

    try {
      // 使用滚动并发策略：始终保持5个任务并发执行，完成1个立即补充1个
      const concurrency = 5;
      const updatedItems = clearAll ? [...itemsToGenerate] : [...leafItems];
      
      // 创建任务队列
      let currentIndex = 0;
      const runningTasks = new Set<Promise<void>>();
      
      // 创建单个任务的执行函数
      const executeTask = async (item: OutlineItem) => {
        // 创建 AbortController
        const abortController = new AbortController();
        abortControllersRef.current.set(item.id, abortController);
        
        try {
          const updatedItem = await generateItemContent(item, outlineData.project_overview || '', abortController);
          
          // 移除 AbortController
          abortControllersRef.current.delete(item.id);
          
          const index = updatedItems.findIndex(ui => ui.id === updatedItem.id);
          if (index !== -1) {
            updatedItems[index] = {
              ...updatedItem,
              content: updatedItem.content || ''
            };
          }
          setProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
        } catch (error) {
          console.error(`生成内容失败 ${item.title}:`, error);
          
          // 移除 AbortController
          abortControllersRef.current.delete(item.id);
          
          const errorMessage = error instanceof Error ? error.message : '生成失败';
          const failedItem = { ...item, error: errorMessage, content: item.content || '' };
          
          const index = updatedItems.findIndex(ui => ui.id === item.id);
          if (index !== -1) {
            updatedItems[index] = failedItem;
          }
          
          setProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
        }
      };
      
      // 启动初始的5个任务
      while (currentIndex < Math.min(concurrency, itemsToGenerate.length)) {
        const task = executeTask(itemsToGenerate[currentIndex]).then(() => {
          runningTasks.delete(task);
        });
        runningTasks.add(task);
        currentIndex++;
      }
      
      // 滚动执行：每完成一个任务，立即启动下一个
      while (currentIndex < itemsToGenerate.length || runningTasks.size > 0) {
        // 检查停止标志
        if (stopGenerationRef.current) {
          showToast('warning', '生成已停止');
          break;
        }
        
        if (runningTasks.size > 0) {
          // 等待任意一个任务完成
          await Promise.race(runningTasks);
          
          // 如果还有待执行的任务且未停止，立即启动一个新任务
          if (currentIndex < itemsToGenerate.length && !stopGenerationRef.current) {
            const task = executeTask(itemsToGenerate[currentIndex]).then(() => {
              runningTasks.delete(task);
            });
            runningTasks.add(task);
            currentIndex++;
          }
        }
      }

      // 更新状态
      setLeafItems(updatedItems);
      
      // 这里需要更新整个outlineData，但由于我们只有props，需要通过回调通知父组件
      // 暂时只更新本地状态
      
    } catch (error) {
      console.error('生成内容时出错:', error);
    } finally {
      setIsGenerating(false);
      setProgress(prev => ({ ...prev, current: '', generating: new Set<string>() }));
    }
  };

  // 获取叶子节点的最新内容（包括生成的内容）
  const getLatestContent = (item: OutlineItem): string => {
    if (!item.children || item.children.length === 0) {
      // 叶子节点，从 leafItems 获取最新内容
      const leafItem = leafItems.find(leaf => leaf.id === item.id);
      return leafItem?.content || item.content || '';
    }
    return item.content || '';
  };

  // 解析Markdown内容为Word段落
  // （已提取到文件顶层，供后续导出Word等复用）

  // 导出Word文档
  const handleExportWord = async () => {
    if (!outlineData) return;

    try {
      // 构建带有最新内容的导出数据（leafItems 中存的是实时内容）
      const buildExportOutline = (items: OutlineItem[]): OutlineItem[] => {
        return items.map(item => {
          const latestContent = getLatestContent(item);
          const exportedItem: OutlineItem = {
            ...item,
            content: latestContent,
          };
          if (item.children && item.children.length > 0) {
            exportedItem.children = buildExportOutline(item.children);
          }
          return exportedItem;
        });
      };

      const exportPayload = {
        project_id: projectId,  // 添加项目ID
        project_name: outlineData.project_name,
        project_overview: outlineData.project_overview,
        outline: buildExportOutline(outlineData.outline),
        annotations: allAnnotations,  // 添加批注数据
        style_config: styleConfig,  // 添加样式配置
      };
      
      const response = await documentApi.exportWord(exportPayload);
      if (!response.ok) {
        throw new Error('导出失败');
      }
      const blob = await response.blob();
      saveAs(blob, `${outlineData.project_name || '标书文档'}.docx`);
      
    } catch (error) {
      console.error('导出失败:', error);
      alert('导出失败，请重试');
    }
  };

  if (!outlineData) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">暂无内容</h3>
            <p className="mt-1 text-sm text-gray-500">
              请先在"目录编辑"步骤中生成目录结构
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 统计章节状态
  const completedItems = leafItems.filter(item => {
    // 有内容且不是错误信息
    if (!item.content) return false;
    const trimmedContent = item.content.trim();
    // 排除以"错误:"或"Error:"开头的内容
    if (trimmedContent.startsWith('错误:') || trimmedContent.startsWith('Error:')) {
      return false;
    }
    // 排除有error字段的
    if (item.error) return false;
    return true;
  }).length;
  
  // 失败的章节数量
  const failedItems = leafItems.filter(item => {
    // 有error字段
    if (item.error) return true;
    // 内容是错误信息
    if (item.content) {
      const trimmedContent = item.content.trim();
      if (trimmedContent.startsWith('错误:') || trimmedContent.startsWith('Error:')) {
        return true;
      }
    }
    return false;
  }).length;
  
  // 待生成 = 总数 - 已完成 - 失败
  const pendingItems = leafItems.length - completedItems - failedItems;

  return (
    <div className="h-full flex overflow-hidden">
      {/* 表格滚动样式 - 全局 */}
      <style>{`
        /* 为所有表格添加滚动容器 */
        .prose table {
          display: block;
          width: 100%;
          overflow-x: auto;
          margin-bottom: 1rem;
        }
        .prose table tbody {
          display: table;
          width: 100%;
        }
        /* 自定义滚动条样式 - 更美观 */
        .prose table::-webkit-scrollbar {
          height: 8px;
        }
        .prose table::-webkit-scrollbar-track {
          background: #F7FAFC;
          border-radius: 4px;
        }
        .prose table::-webkit-scrollbar-thumb {
          background: #CBD5E0;
          border-radius: 4px;
        }
        .prose table::-webkit-scrollbar-thumb:hover {
          background: #A0AEC0;
        }
      `}</style>
      
      {/* 左侧小盒子 - flex占满剩余空间，最小120px */}
      <div className="flex-1 min-w-[90px]" />

      {/* 中间大盒子：目录+内容，固定宽度，不压缩 */}
      <div className="flex h-full flex-shrink-0">
          {/* 左侧目录导航 - 固定320px，隐藏时保持占位 */}
          <div 
            className="bg-white border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0 transition-all duration-300"
            style={{ 
              width: '320px',
              visibility: showOutlineNav ? 'visible' : 'hidden',
              opacity: showOutlineNav ? 1 : 0
            }}
          >
            {/* 目录标题 */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <Bars3Icon className="w-5 h-5 mr-2 text-gray-500" />
                标书目录
              </h3>
              <button
                onClick={() => setShowOutlineNav(false)}
                className="text-gray-400 hover:text-gray-600"
                title="隐藏目录"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* 目录列表 - 隐藏滚动条 */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <style>{`
                .flex-1.overflow-y-auto::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {outlineData?.outline && renderOutlineNav(outlineData.outline)}
            </div>
          </div>

          {/* 主内容区 - 固定900px宽度，滚动条紧贴内容 */}
          <div className="flex flex-col flex-shrink-0 relative" style={{ width: '900px' }}>
            {/* 文档内容区 - 独立滚动 */}
            <div className="flex-1 overflow-y-scroll" id="content-scroll" style={{ 
              scrollbarWidth: 'thin',
              scrollbarColor: '#CBD5E0 #F7FAFC'
            }}>
              <style>{`
                #content-scroll::-webkit-scrollbar {
                  width: 8px;
                }
                #content-scroll::-webkit-scrollbar-track {
                  background: #F7FAFC;
                }
                #content-scroll::-webkit-scrollbar-thumb {
                  background: #CBD5E0;
                  border-radius: 4px;
                }
                #content-scroll::-webkit-scrollbar-thumb:hover {
                  background: #A0AEC0;
                }
              `}</style>
              <div className="px-6">
            {/* 顶部工具栏 - 智能显示/隐藏 */}
            <div 
              className="bg-white rounded-lg shadow mb-6 sticky z-10 transition-all duration-300"
              style={{
                top: showTopBar ? '0' : '-300px',
                opacity: showTopBar ? 1 : 0,
                transform: showTopBar ? 'translateY(0)' : 'translateY(-100%)'
              }}
            >
            <div className="px-6 pt-5 pb-3 border-b border-gray-200">
              {/* 第一行：项目名称 + 按钮 */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center space-x-3">
                  {!showOutlineNav && (
                    <button
                      onClick={() => setShowOutlineNav(true)}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="显示目录"
                    >
                      <Bars3Icon className="w-6 h-6" />
                    </button>
                  )}
                  <h2 
                    className="text-lg font-semibold text-gray-900 truncate max-w-md" 
                    title={projectName || outlineData.project_name || '标书内容'}
                  >
                    {projectName || outlineData.project_name || '标书内容'}
                  </h2>
                </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={isGenerating ? handleStopGeneration : handleGenerateContent}
                disabled={false}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isGenerating 
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                }`}
              >
                {isGenerating ? (
                  <>
                    <StopIcon className="w-4 h-4 mr-2" />
                    停止生成
                  </>
                ) : (
                  <>
                    <PlayIcon className="w-4 h-4 mr-2" />
                    全部生成
                  </>
                )}
              </button>
              
              <button
                onClick={() => setShowStyleDialog(true)}
                disabled={isGenerating}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="样式设置"
              >
                <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                设置
              </button>

              <button
                onClick={handleExportWord}
                disabled={isGenerating}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                导出
              </button>
            </div>
          </div>
          
          {/* 第二行：统计信息（不换行，横向延伸，左侧与项目名称对齐） */}
          <div className="flex items-center space-x-4 text-sm text-gray-600" style={{ marginLeft: !showOutlineNav ? '36px' : '0px' }}>
            <div className="flex items-center">
              <DocumentTextIcon className="w-4 h-4 text-blue-500 mr-1" />
              <span>共 {leafItems.length} 个章节</span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="w-4 h-4 text-green-500 mr-1" />
              <span>已完成: {completedItems}</span>
            </div>
            <div className="flex items-center">
              <DocumentTextIcon className="w-4 h-4 text-gray-400 mr-1" />
              <span>待生成: {pendingItems}</span>
            </div>
            {failedItems > 0 && (
              <div className="flex items-center">
                <ExclamationCircleIcon className="w-4 h-4 text-red-500 mr-1" />
                <span className="text-red-600">失败: {failedItems}</span>
              </div>
            )}
            <div className="flex items-center">
              <DocumentTextIcon className="w-4 h-4 text-purple-500 mr-1" />
              <span>总字数: {leafItems.reduce((sum, item) => sum + (item.content?.length || 0), 0)}</span>
            </div>
          </div>
          
          {/* 进度条 */}
          {isGenerating && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>正在生成: {progress.current}</span>
                <span>{progress.completed} / {progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 文档内容 */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-8">
          <div className="prose max-w-none">
            {/* 目录结构和内容 */}
            <div className="space-y-8" style={{
              '--heading1-font': styleConfig.heading1.fontFamily,
              '--heading1-size': `${styleConfig.heading1.fontSize}px`,
              '--heading1-color': styleConfig.heading1.color,
              '--heading1-line-height': styleConfig.heading1.lineHeight,
              '--heading2-font': styleConfig.heading2.fontFamily,
              '--heading2-size': `${styleConfig.heading2.fontSize}px`,
              '--heading2-color': styleConfig.heading2.color,
              '--heading2-line-height': styleConfig.heading2.lineHeight,
              '--heading3-font': styleConfig.heading3.fontFamily,
              '--heading3-size': `${styleConfig.heading3.fontSize}px`,
              '--heading3-color': styleConfig.heading3.color,
              '--heading3-line-height': styleConfig.heading3.lineHeight,
              '--body-font': styleConfig.body.fontFamily,
              '--body-size': `${styleConfig.body.fontSize}px`,
              '--body-color': styleConfig.body.color,
              '--body-line-height': styleConfig.body.lineHeight,
            } as React.CSSProperties}>
              {renderOutline(outlineData.outline)}
            </div>
          </div>
        </div>

              </div> {/* px-6容器结束 */}
            </div> {/* 内容滚动区结束 id="content-scroll" */}
            {/* 回到顶部按钮 - 固定在内容区右下角，滚动条旁 */}
            {showScrollToTop && (
              <button
                onClick={scrollToTop}
                className="absolute bottom-8 right-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition-all duration-200 hover:shadow-xl z-[60]"
                aria-label="回到顶部"
                title="回到顶部"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </button>
            )}
          </div> {/* 主内容区900px结束 */}
        </div> {/* flex h-full容器结束 */}
      </div> {/* 中间大盒子结束 */}
      
      {/* 右侧小盒子 - flex占满剩余空间，最小90px，相对定位容器用于批注导航条 */}
      <div 
        className="flex-1 min-w-[90px] relative h-full overflow-hidden" 
        id="right-panel"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {/* 批注面板容器 */}
        <div 
          id="annotation-panel-container" 
          className="absolute inset-0"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {showAnnotationPanel && (
            <div 
              className="absolute inset-0 bg-gray-50/95 border-l border-gray-200 z-[99] flex flex-col"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {/* 批注面板头部 */}
              <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 bg-yellow-50 border-b border-yellow-200 gap-2">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-yellow-800 whitespace-nowrap truncate">批注面板</h3>
                  <p className="text-xs text-yellow-600 whitespace-nowrap truncate">查看和管理所有批注</p>
                </div>
                <button
                  onClick={() => setShowAnnotationPanel(false)}
                  className="p-1 text-yellow-700 hover:bg-yellow-200 rounded-lg transition-colors flex-shrink-0"
                  title="收起批注面板"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {/* 批注内容区域 - Portal 渲染批注卡片到这里 */}
              <div 
                id="annotation-list-container" 
                className="flex-1 relative overflow-hidden"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              />
            </div>
          )}
        </div>
        
        {/* 展开批注面板按钮 - 当面板收起时显示 */}
        {!showAnnotationPanel && (
          <button
            onClick={() => setShowAnnotationPanel(true)}
            className="absolute top-4 right-4 z-[100] bg-yellow-500 text-white p-2 rounded-lg hover:bg-yellow-600 transition-all shadow-md"
            title="展开批注面板"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          </button>
        )}
        
      {/* AI 智能助手 - 可拖动、可折叠的浮动面板 */}
      {/* 面板始终渲染但用CSS控制显示/隐藏，确保分数计算逻辑执行 */}
      <div className={aiPanelExpanded ? 'block' : 'hidden'}>
        <AIAssistantPanel
          position={aiPanelPosition}
          onPositionChange={setAiPanelPosition}
          onCollapse={() => setAiPanelExpanded(false)}
          projectId={projectId} // 新增：传递项目ID用于真实检查
          onScrollToSection={scrollToSection} // 新增：传递滚动函数
          onScoreChange={setAiCheckScore} // 将健康分数同步到小球
        />
      </div>
      
      {/* 折叠状态 - AI 助手图标 */}
      {!aiPanelExpanded && (
        <div
          className="fixed z-[300]"
          style={{
            left: Math.min(aiPanelPosition.x, window.innerWidth - 58),
            top: aiPanelPosition.y,
          }}
        >
          {/* AI 助手球 */}
          {(() => {
            const score = aiCheckScore;
            let bubbleGradient = 'from-emerald-400 via-emerald-500 to-emerald-600';
            if (score < 60) {
              bubbleGradient = 'from-rose-400 via-rose-500 to-rose-600';
            } else if (score < 85) {
              bubbleGradient = 'from-amber-400 via-amber-500 to-amber-600';
            }

            return (
              <button
                onMouseDown={(e) => {
                  setHasDragged(false);
                  // 设置定时器，150ms 后启动拖动模式
                  aiPanelLongPressTimerRef.current = setTimeout(() => {
                    setIsDraggingAiPanel(true);
                    aiPanelDragStartRef.current = {
                      x: e.clientX,
                      y: e.clientY,
                      panelX: aiPanelPosition.x,
                      panelY: aiPanelPosition.y,
                    };
                  }, 150);
                }}
                onMouseUp={() => {
                  // 清除定时器
                  if (aiPanelLongPressTimerRef.current) {
                    clearTimeout(aiPanelLongPressTimerRef.current);
                    aiPanelLongPressTimerRef.current = null;
                  }
                  // 如果没有拖动，则展开面板
                  if (!hasDragged) {
                    setAiPanelExpanded(true);
                  }
                  setIsDraggingAiPanel(false);
                }}
                onMouseLeave={() => {
                  // 鼠标移出时清除定时器
                  if (aiPanelLongPressTimerRef.current) {
                    clearTimeout(aiPanelLongPressTimerRef.current);
                    aiPanelLongPressTimerRef.current = null;
                  }
                }}
                className={`w-12 h-12 rounded-full p-[6px] bg-gradient-to-br ${bubbleGradient} shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-110 relative cursor-pointer`}
                title="点击展开，长按拖动"
              >
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center relative overflow-hidden">
                  {/* 雷达扫描效果 */}
                  <div
                    className="absolute inset-0 rounded-full border-2 border-sky-400 border-t-transparent border-l-transparent opacity-40 animate-spin"
                  />
                  <span className="relative z-10 text-gray-800 text-lg font-semibold leading-none">{score}</span>
                  {/* 光晕动画 */}
                  <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-30 group-hover:animate-ping" />
                </div>
              </button>
            );
          })()}
        </div>
      )}

      {/* 拖动事件监听 */}
      {isDraggingAiPanel && (
        <div
          className="fixed inset-0 z-[400] cursor-move"
          onMouseMove={(e) => {
            const deltaX = e.clientX - aiPanelDragStartRef.current.x;
            const deltaY = e.clientY - aiPanelDragStartRef.current.y;
            // 超过 5px 移动距离才算拖动
            if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
              setHasDragged(true);
            }
            // 允许拖动到整个浏览器窗口
            const newX = Math.max(-40, Math.min(window.innerWidth - 10, aiPanelDragStartRef.current.panelX + deltaX));
            const newY = Math.max(-40, Math.min(window.innerHeight - 10, aiPanelDragStartRef.current.panelY + deltaY));
            setAiPanelPosition({ x: newX, y: newY });
          }}
          onMouseUp={() => setIsDraggingAiPanel(false)}
          onMouseLeave={() => setIsDraggingAiPanel(false)}
        />
      )}
      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText || "确定"}
        cancelText={confirmDialog.cancelText || "取消"}
        showSecondButton={confirmDialog.showSecondButton}
        secondButtonText={confirmDialog.secondButtonText}
        secondButtonAction={confirmDialog.secondButtonAction}
        confirmButtonClass="bg-blue-600 hover:bg-blue-700"
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel || (() => setConfirmDialog(prev => ({ ...prev, isOpen: false })))}
      />

      {/* 全局批注导航竖条 - 显示所有章节的批注分布，固定在右侧小盒子右边缘 */}
      {allAnnotations.length > 0 && showAnnotationNav && (
        <div 
          className="absolute right-0 top-0 bottom-0 w-3 bg-gray-300/50 z-[200]"
          title={`共 ${allAnnotations.length} 个批注`}
        >
          {/* 批注位置标记 - 基于文档总高度计算比例位置 */}
          {(() => {
            const scrollContainer = document.getElementById('content-scroll');
            if (!scrollContainer) return null;
            
            const documentHeight = scrollContainer.scrollHeight || 1000;
            
            return allAnnotations.map((annotation, index) => {
              const mark = document.querySelector(`[data-annotation-id="${annotation.id}"]`);
              if (!mark) {
                // 如果找不到标记，按索引均匀分布
                const ratio = index / Math.max(allAnnotations.length - 1, 1);
                const position = 5 + ratio * 90;
                return (
                  <div
                    key={`nav-${annotation.id}`}
                    className="absolute left-0.5 right-0.5 bg-yellow-400 hover:bg-yellow-500 transition-colors cursor-pointer opacity-50"
                    style={{
                      top: `${position}%`,
                      height: '6px',
                      borderRadius: '2px',
                    }}
                    onClick={() => scrollToAnnotationMark(annotation.id)}
                    title={`批注: ${annotation.content?.substring(0, 30) || ''}...`}
                  />
                );
              }
              
              // 计算标记在文档中的绝对位置
              const markRect = mark.getBoundingClientRect();
              const containerRect = scrollContainer.getBoundingClientRect();
              const markAbsoluteTop = markRect.top - containerRect.top + scrollContainer.scrollTop;
              
              // 计算在竖条中的比例位置 (5% - 95%)
              const ratio = markAbsoluteTop / documentHeight;
              const position = 5 + ratio * 90;
              
              return (
                <div
                  key={`nav-${annotation.id}`}
                  className="absolute left-0.5 right-0.5 bg-yellow-400 hover:bg-yellow-500 transition-colors cursor-pointer"
                  style={{
                    top: `${Math.max(2, Math.min(98, position))}%`,
                    height: '6px',
                    borderRadius: '2px',
                  }}
                  onClick={() => scrollToAnnotationMark(annotation.id)}
                  title={`批注: ${annotation.content?.substring(0, 30) || ''}...`}
                />
              );
            });
          })()}
        </div>
      )}

      </div> {/* 右侧小盒子结束 */}

      {/* 批注导航竖条闪烁样式 */}
      <style>{`
        .annotation-flash {
          animation: annotation-flash-anim 0.5s ease-in-out 2;
        }
        @keyframes annotation-flash-anim {
          0%, 100% { background-color: rgba(253, 224, 71, 0.6); }
          50% { background-color: rgba(253, 224, 71, 1); }
        }
        
        /* 应用样式配置到标题和正文 */
        .space-y-8 h2 {
          font-family: var(--heading1-font) !important;
          font-size: var(--heading1-size) !important;
          color: var(--heading1-color) !important;
          line-height: var(--heading1-line-height) !important;
        }
        .space-y-8 h3 {
          font-family: var(--heading2-font) !important;
          font-size: var(--heading2-size) !important;
          color: var(--heading2-color) !important;
          line-height: var(--heading2-line-height) !important;
        }
        .space-y-8 h4 {
          font-family: var(--heading3-font) !important;
          font-size: var(--heading3-size) !important;
          color: var(--heading3-color) !important;
          line-height: var(--heading3-line-height) !important;
        }
        .space-y-8 .prose,
        .space-y-8 .prose p,
        .space-y-8 .prose div:not([class*="fixed"]) {
          font-family: var(--body-font) !important;
          font-size: var(--body-size) !important;
          color: var(--body-color) !important;
          line-height: var(--body-line-height) !important;
        }
      `}</style>
      
      {/* 资源关联面板 - 弹框显示 */}
      {activeResourceChapter && (
        <div 
          className="fixed inset-0 z-[500] flex items-center justify-center bg-black bg-opacity-40 overflow-hidden"
          onWheel={(e) => e.stopPropagation()}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setActiveResourceChapter(null);
              setActiveResourceChapterData(null);
            }
          }}
        >
          {/* 弹框面板 */}
          <div className="bg-white rounded-xl shadow-2xl border border-blue-200 w-[840px] max-w-[90vw] max-h-[85vh] flex flex-col">
            
            {/* 头部 */}
            <div className="flex items-center justify-between p-4 pb-3 border-b border-gray-200">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <LinkIcon className="w-5 h-5 text-blue-500" />
                  <h3 className="text-base font-semibold text-gray-800">生成资源关联</h3>
                </div>
                {/* 字数信息提示 */}
                {(() => {
                  // 查找当前章节
                  const findItem = (items: OutlineItem[], id: string): OutlineItem | null => {
                    for (const item of items) {
                      if (item.id === id) return item;
                      if (item.children) {
                        const found = findItem(item.children, id);
                        if (found) return found;
                      }
                    }
                    return null;
                  };
                  const activeItem = outlineData ? findItem(outlineData.outline, activeResourceChapter) : null;
                  
                  if (!activeItem) return null;
                  
                  // 获取当前章节的实际字数
                  const currentContent = getLeafItemContent(activeItem.id) || '';
                  const currentWordCount = calculateActualWordCount(currentContent);
                  
                  return (
                    <div className="mt-1.5 flex items-center gap-3 text-xs pl-7">
                      {/* 目标字数显示/编辑 */}
                      {editingResourceWordCount === activeResourceChapter ? (
                        // 编辑模式
                        <div className="inline-flex items-center gap-1">
                          <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-gray-600">目标:</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={tempResourceWordCount}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^\d]/g, '');
                              setTempResourceWordCount(value);
                            }}
                            onBlur={() => {
                              if (tempResourceWordCount.trim()) {
                                handleSaveResourceWordCount(activeItem);
                              } else {
                                setEditingResourceWordCount(null);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveResourceWordCount(activeItem);
                              } else if (e.key === 'Escape') {
                                setEditingResourceWordCount(null);
                              }
                            }}
                            className="w-16 px-1 py-0.5 text-xs text-center bg-gray-50 rounded outline-none focus:bg-white border border-gray-300"
                            autoFocus
                            placeholder="0"
                          />
                          <span className="text-gray-600">字</span>
                          <button
                            onMouseDown={(e) => {
                              e.preventDefault();
                            }}
                            onClick={() => handleSaveResourceWordCount(activeItem)}
                            className="p-0.5 text-green-600 hover:bg-green-100 rounded"
                            title="保存"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        // 显示模式
                        <span 
                          className="inline-flex items-center text-gray-600 hover:text-blue-600 cursor-pointer hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors group"
                          onClick={() => {
                            setEditingResourceWordCount(activeResourceChapter);
                            setTempResourceWordCount(String(activeItem.expected_word_count || ''));
                          }}
                          title="点击编辑目标字数"
                        >
                          <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          目标: {activeItem.expected_word_count || 0}字
                          <svg className="w-3 h-3 ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </span>
                      )}
                      
                      {/* 当前字数显示 */}
                      <span className="inline-flex items-center text-gray-600">
                        <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        当前: <span className={currentWordCount > 0 ? 'text-green-600 font-medium' : ''}>{currentWordCount}</span>字
                      </span>
                      
                      {/* 锁定状态 */}
                      {activeItem.is_locked && (
                        <span className="inline-flex items-center text-amber-600">
                          <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                          </svg>
                          已锁定
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <button
                onClick={() => {
                  setActiveResourceChapter(null);
                  setActiveResourceChapterData(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="关闭"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* 滚动内容区 */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* 第一部分：招标文件和材料 */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <span className="inline-block w-1 h-4 bg-blue-500 rounded mr-2"></span>
                  招标文件与材料
                </h4>
                <div className="space-y-3 pl-3">
                  {/* 招标文件 */}
                  {projectFiles.tender && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1.5">招标文件</p>
                      <label className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-gray-700 flex-1 truncate">{projectFiles.tender.file_name}</span>
                      </label>
                    </div>
                  )}
                  
                  {/* 其他资料 */}
                  {projectFiles.others.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1.5">其他资料</p>
                      <div className="space-y-1.5">
                        {projectFiles.others.map((file: any) => {
                          const isChecked = getChapterResource(activeResourceChapter).selected_other_materials?.includes(file.id) || false;
                          
                          return (
                            <label key={file.id} className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  // 纯本地状态更新，保存时统一提交
                                  setChapterResources(prev => {
                                    const current = prev[activeResourceChapter]?.selected_other_materials || [];
                                    const isSelected = current.includes(file.id);
                                    const newSelected = isSelected
                                      ? current.filter((id: string) => id !== file.id)
                                      : [...current, file.id];
                                    return {
                                      ...prev,
                                      [activeResourceChapter]: {
                                        ...prev[activeResourceChapter],
                                        selected_other_materials: newSelected,
                                        use_other_materials: newSelected.length > 0
                                      }
                                    };
                                  });
                                }}
                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                              />
                              <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                              </svg>
                              <span className="text-sm text-gray-700 flex-1 truncate">{file.file_name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* 没有文件的提示 */}
                  {!projectFiles.tender && projectFiles.others.length === 0 && (
                    <p className="text-sm text-gray-400 italic py-2">暂无上传文件</p>
                  )}
                </div>
              </div>
              
              {/* 第二部分：解析数据 */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <span className="inline-block w-1 h-4 bg-green-500 rounded mr-2"></span>
                  解析数据
                </h4>
                <div className="flex flex-wrap gap-2 pl-3">
                  <label className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 px-3 py-2 rounded transition-colors border border-gray-200">
                    <input
                      type="checkbox"
                      checked={getChapterResource(activeResourceChapter).use_project_overview}
                      onChange={() => handleToggleResourceOption(activeResourceChapter, 'use_project_overview')}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 whitespace-nowrap">项目概述</span>
                  </label>
                  
                  <label className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 px-3 py-2 rounded transition-colors border border-gray-200">
                    <input
                      type="checkbox"
                      checked={getChapterResource(activeResourceChapter).use_tech_requirements}
                      onChange={() => handleToggleResourceOption(activeResourceChapter, 'use_tech_requirements')}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 whitespace-nowrap">技术评分</span>
                    {getChapterResource(activeResourceChapter).suggested?.tech_requirements && (
                      <span className="text-xs text-green-600">(推荐)</span>
                    )}
                  </label>
                  
                  <label className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 px-3 py-2 rounded transition-colors border border-gray-200">
                    <input
                      type="checkbox"
                      checked={getChapterResource(activeResourceChapter).use_disqualification}
                      onChange={() => handleToggleResourceOption(activeResourceChapter, 'use_disqualification')}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 whitespace-nowrap">废标项</span>
                  </label>
                  
                  <label className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 px-3 py-2 rounded transition-colors border border-gray-200">
                    <input
                      type="checkbox"
                      checked={getChapterResource(activeResourceChapter).use_quotation}
                      onChange={() => handleToggleResourceOption(activeResourceChapter, 'use_quotation')}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 whitespace-nowrap">分项报价</span>
                    {getChapterResource(activeResourceChapter).suggested?.quotation && (
                      <span className="text-xs text-green-600">(推荐)</span>
                    )}
                  </label>
                  
                  <label className="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 px-3 py-2 rounded transition-colors border border-gray-200">
                    <input
                      type="checkbox"
                      checked={getChapterResource(activeResourceChapter).use_templates}
                      onChange={() => handleToggleResourceOption(activeResourceChapter, 'use_templates')}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 whitespace-nowrap">招标模版</span>
                    {getChapterResource(activeResourceChapter).suggested?.templates && (
                      <span className="text-xs text-green-600">(推荐)</span>
                    )}
                  </label>
                </div>
              </div>
              
              {/* 第三部分：上传文件 */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <span className="inline-block w-1 h-4 bg-purple-500 rounded mr-2"></span>
                  补充材料
                </h4>
                <div className="pl-3">
                  {/* 隐藏的文件输入框 */}
                  <input
                    ref={supplementFileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => handleSupplementFileUpload(e.target.files)}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                  />
                  
                  {/* 上传区域 */}
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors cursor-pointer"
                    onClick={handleSupplementUploadClick}
                    onDragOver={handleSupplementDragOver}
                    onDrop={handleSupplementDrop}
                  >
                    <div className="flex flex-col items-center text-center">
                      {uploadingSupplement ? (
                        <>
                          <svg className="w-8 h-8 text-blue-500 mb-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <p className="text-sm text-blue-600">上传中...</p>
                        </>
                      ) : (
                        <>
                          <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className="text-sm text-gray-600">点击或拖拽上传补充文件</p>
                          <p className="text-xs text-gray-400 mt-1">支持 PDF、Word、Excel、图片等格式</p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* 已上传文件列表 */}
                  {supplementFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {supplementFiles.map((file) => (
                        <div key={file.id} className="p-3 bg-gray-50 rounded space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                {/* 状态图标 */}
                                {(file.status === 'pending' || file.status === 'processing') && (
                                  <svg className="animate-spin w-4 h-4 text-blue-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                )}
                                {file.status === 'completed' && (
                                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                                {file.status === 'failed' && (
                                  <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                )}
                                <div className="text-sm text-gray-900 font-medium truncate">{file.name}</div>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {(file.size / 1024).toFixed(1)} KB
                                {/* 状态文本 */}
                                {file.status === 'pending' && (
                                  <span className="ml-2 text-blue-600">• 等待处理</span>
                                )}
                                {file.status === 'processing' && (
                                  <span className="ml-2 text-blue-600">• 处理中</span>
                                )}
                                {file.status === 'completed' && file.processing_method && (
                                  <span className="ml-2">
                                    • {file.processing_method === 'original' ? '原始' : 
                                       file.processing_method === 'ai_summary' ? 'AI摘要' : '已完成'}
                                  </span>
                                )}
                                {file.status === 'failed' && (
                                  <span className="ml-2 text-red-600">• 处理失败</span>
                                )}
                                {file.summary_length && (
                                  <span className="ml-2">• 提取: {file.summary_length}字</span>
                                )}
                              </div>
                            </div>
                            <button 
                              onClick={() => handleDeleteSupplementFile(file.id)}
                              className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                              title="删除"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          <input
                            type="text"
                            placeholder="用途说明（选填）"
                            defaultValue={file.usage_note}
                            onBlur={(e) => handleUpdateSupplementUsage(file.id, e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* 第四部分：AI生成指导 */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <span className="inline-block w-1 h-4 bg-orange-500 rounded mr-2"></span>
                  AI生成指导
                </h4>
                <div className="pl-3 space-y-3">
                  {/* 模型选择 */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1.5">模型选择 &nbsp;&nbsp; (默认使用项目配置的模型，也可为本章节单独指定模型)</label>
                    <select
                      value={getChapterResource(activeResourceChapter).selected_model || selectedModel}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setChapterResources(prev => ({
                          ...prev,
                          [activeResourceChapter]: {
                            ...prev[activeResourceChapter],
                            selected_model: newValue
                          }
                        }));
                        // selected_model 在点击“保存资源配置”时一并持久化到后端
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={selectedModel}>项目默认：{selectedModel}</option>
                      {availableTextModels.length > 0 && (
                        <>
                          <option disabled>──────</option>
                          {TEXT_MODELS_CONFIG.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name} ({m.company})
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>
                  
                  {/* 生成指导文本框 */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1.5">生成偏向 &nbsp;&nbsp; (AI将根据您的指导优化生成内容的风格和侧重点)</label>
                    <textarea
                      value={getChapterResource(activeResourceChapter).generation_preferences?.guidance || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setChapterResources(prev => ({
                          ...prev,
                          [activeResourceChapter]: {
                            ...prev[activeResourceChapter],
                            generation_preferences: {
                              ...prev[activeResourceChapter]?.generation_preferences,
                              guidance: newValue
                            }
                          } as any
                        }));
                      }}
                      placeholder="输入生成偏向和要求，例如：重点突出技术优势，强调项目经验..."
                      className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* 底部操作按钮 */}
            <div className="p-4 pt-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-end gap-3">
                <button 
                  onClick={() => {
                    setActiveResourceChapter(null);
                    setActiveResourceChapterData(null);
                  }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  取消
                </button>
                <button 
                  onClick={async () => {
                    // 先保存资源配置
                    await handleSaveResourceConfig(activeResourceChapter);
                    // 关闭弹框
                    setActiveResourceChapter(null);
                    setActiveResourceChapterData(null);
                    // 执行重写 - 找到当前章节数据并调用重写
                    if (activeResourceChapterData) {
                      handleRewriteChapter(activeResourceChapterData);
                    }
                  }}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  重写
                </button>
                <button 
                  onClick={() => {
                    handleSaveResourceConfig(activeResourceChapter);
                    setActiveResourceChapter(null);
                    setActiveResourceChapterData(null);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 样式设置对话框 */}
      <StyleSettingsDialog
        isOpen={showStyleDialog}
        onClose={() => setShowStyleDialog(false)}
        onSave={async (newConfig, newGlobalConfig) => {
          setStyleConfig(newConfig);
          localStorage.setItem('bidding_style_config', JSON.stringify(newConfig));
          
          if (newGlobalConfig) {
            setGlobalConfig(newGlobalConfig);
            localStorage.setItem('bidding_global_config', JSON.stringify(newGlobalConfig));
            
            // 保存到后端
            if (projectId) {
              try {
                await projectApi.saveAIDirection(projectId, newGlobalConfig.aiDirection);
              } catch (error) {
                console.error('保存AI生成方向失败:', error);
              }
            }
          }
        }}
        initialConfig={styleConfig}
        initialGlobalConfig={globalConfig}
        maxHeadingLevel={outlineData ? calculateMaxLevel(outlineData.outline) : 3}
      />
      
      {/* Toast通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={closeToast}
      />
      
      {/* Token 欠费弹窗 */}
      <TokenAlertDialog
        isOpen={tokenAlert.show}
        type={tokenAlert.type}
        title={tokenAlert.title}
        message={tokenAlert.message}
        onRecharge={rechargeTokens}
        onClose={closeTokenAlert}
      />
    </div>
  );
};

export default ContentEdit;